import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Map, { MapRef, Marker } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import {
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Form,
    Image,
    Input,
    InputNumber,
    Row,
    Radio,
    Segmented,
    Select,
    Slider,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    EnvironmentOutlined,
    HomeOutlined,
    PartitionOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import IndoorAreaMap, { SlamMapData, IndoorGeomResult, IndoorMapMarker } from '../components/Map/IndoorAreaMap';
import SlamMapOverlay, { SlamOverlayData, SlamPlacement } from '../components/Map/SlamMapOverlay';
import '../style/AreaEditor.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';

const { Title, Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

interface AreaType {
    id: number;
    name: string;
    maxVertices?: number | null;
    spaceType?: string | null; // 'outdoor' | 'indoor' | 'mixed' | null
    templateDimensions?: {
        widthMeters: number;
        heightMeters: number;
    } | null;
}

interface AreaListItem {
    id: number;
    name: string;
    spaceType?: string;
    parentAreaId?: number | null;
    slamMapId?: number | null;
}

interface SlamMapOption {
    id: number;
    name: string;
    mapType: string;
    imageUrl?: string | null;
    imageStoragePath?: string | null;
    createdAt: string;
    areaId?: number | null;
}

interface SearchResult {
    id: string;
    name: string;
    center: [number, number];
    place_name: string;
}

interface GeneralMarker {
    id: number;
    label?: string | null;
    areaId?: number | null;
    slamMapId?: number | null;
    markerType?: string | null;
    category?: { key?: string | null; name?: string | null; style?: Record<string, any> | null } | null;
    geom?: any | null;
    localPose?: { x?: number | null; y?: number | null; [key: string]: any } | null;
    createdAt?: string | null;
}

interface MissionWaypointOverlay {
    id: string | number;
    label: string;
    lat: number;
    lng: number;
}

interface CompanyRobotOverlay {
    id: number;
    label: string;
    lat: number;
    lng: number;
    status?: string | null;
    lastHeartbeat?: string | null;
}

type SpaceType = 'outdoor' | 'indoor' | 'mixed';
type GeomMode = 'polygon' | 'point';
type EditStage = 'slamPlacement' | 'anchorPoint' | 'areaGeometry';

const DEFAULT_VIEW = { longitude: -0.1278, latitude: 51.5074, zoom: 13 };
const isIndoorLikeSpaceType = (value: SpaceType) => value === 'indoor' || value === 'mixed';

const safeMissionReturnPath = (value: string | null): string | null => {
    if (!value) return null;
    try {
        const baseUrl = 'https://oscar.local';
        const parsed = new URL(value, baseUrl);
        if (parsed.origin !== baseUrl || parsed.pathname !== '/operations') return null;
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return null;
    }
};

const appendCreatedAreaId = (returnPath: string, areaId: number): string => {
    const parsed = new URL(returnPath, 'https://oscar.local');
    parsed.searchParams.set('createdAreaId', String(areaId));
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

const missionIdFromReturnPath = (returnPath: string | null): number | null => {
    if (!returnPath) return null;
    const parsed = new URL(returnPath, 'https://oscar.local');
    const missionId = Number(parsed.searchParams.get('resumeMissionId'));
    return Number.isInteger(missionId) && missionId > 0 ? missionId : null;
};

const gpsPointFromValue = (value: any): { lat: number; lng: number } | null => {
    const toCoordinate = (candidate: unknown): number => (
        candidate === null || candidate === undefined || candidate === ''
            ? Number.NaN
            : Number(candidate)
    );
    const isValidGpsPoint = (lat: number, lng: number) => (
        Number.isFinite(lat)
        && Number.isFinite(lng)
        && lat >= -90
        && lat <= 90
        && lng >= -180
        && lng <= 180
    );

    const resolvedLat = toCoordinate(value?.resolvedLocation?.lat);
    const resolvedLng = toCoordinate(value?.resolvedLocation?.lng);
    if (isValidGpsPoint(resolvedLat, resolvedLng)) {
        return { lat: resolvedLat, lng: resolvedLng };
    }

    const geom = value?.geom || value?.lastLocation || value?.location;
    const coordinates = geom?.type === 'Point' && Array.isArray(geom.coordinates)
        ? geom.coordinates
        : null;
    const lng = toCoordinate(coordinates?.[0]);
    const lat = toCoordinate(coordinates?.[1]);
    return isValidGpsPoint(lat, lng) ? { lat, lng } : null;
};

// ─── GPS ↔ Local metres conversion (matches IndoorAreaMap math) ───────────────

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;

/** Convert a GPS coordinate back to local metres relative to an anchor point. */
function gpsToLocalMeters(
    lat: number, lng: number,
    aLat: number, aLng: number, bDeg: number,
): [number, number] {
    const dlat = (lat - aLat) * DEG_TO_RAD * EARTH_RADIUS;
    const dlng = (lng - aLng) * DEG_TO_RAD * EARTH_RADIUS * Math.cos(aLat * DEG_TO_RAD);
    const b = bDeg * DEG_TO_RAD;
    const my = dlat * Math.cos(b) + dlng * Math.sin(b);
    const mx = -dlat * Math.sin(b) + dlng * Math.cos(b);
    return [mx, my];
}

function localMetersToGps(
    mx: number, my: number,
    aLat: number, aLng: number, bDeg: number,
): [number, number] {
    const b = bDeg * DEG_TO_RAD;
    const northM = my * Math.cos(b) - mx * Math.sin(b);
    const eastM = my * Math.sin(b) + mx * Math.cos(b);
    const lat = aLat + (northM / EARTH_RADIUS) * (180 / Math.PI);
    const lng = aLng + (eastM / (EARTH_RADIUS * Math.cos(aLat * DEG_TO_RAD))) * (180 / Math.PI);
    return [lng, lat];
}

function isGpsCoordinate(coord: any): boolean {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const [lng, lat] = coord;
    return Number.isFinite(lng) && Number.isFinite(lat)
        && lng >= -180 && lng <= 180
        && lat >= -90 && lat <= 90;
}

function isGpsGeometry(geom: any): boolean {
    if (!geom?.coordinates) return false;
    if (geom.type === 'Point') return isGpsCoordinate(geom.coordinates);
    if (geom.type === 'Polygon') return Array.isArray(geom.coordinates?.[0]) && geom.coordinates[0].every(isGpsCoordinate);
    return false;
}

function gpsGeometryToLocalMeters(geom: any, anchor: SlamPlacement | null): any | null {
    if (!geom || !anchor) return null;
    if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
        const [lng, lat] = geom.coordinates;
        return { type: 'Point', coordinates: gpsToLocalMeters(lat, lng, anchor.lat, anchor.lng, anchor.bearingDegrees) };
    }
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
        return {
            type: 'Polygon',
            coordinates: [
                geom.coordinates[0].map(([lng, lat]: [number, number]) =>
                    gpsToLocalMeters(lat, lng, anchor.lat, anchor.lng, anchor.bearingDegrees)),
            ],
        };
    }
    return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AreaCreate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const areaIdFromUrl = searchParams.get('areaId');
    const parentIdFromUrl = searchParams.get('parentId');
    const missionReturnPath = useMemo(
        () => safeMissionReturnPath(searchParams.get('returnTo')),
        [searchParams]
    );
    const resumeMissionId = useMemo(
        () => missionIdFromReturnPath(missionReturnPath),
        [missionReturnPath]
    );
    const currentCompanyId = currentUser?.currentCompanyId || currentUser?.companyId || null;
    const isEditMode = !!areaIdFromUrl;

    const mapRef = useRef<MapRef | null>(null);
    const drawRef = useRef<MapboxDraw | null>(null);
    const clickListenerRef = useRef<((e: any) => void) | null>(null);

    // Form state
    const [form] = Form.useForm();
    const [areaTypes, setAreaTypes] = useState<AreaType[]>([]);
    const [areas, setAreas] = useState<AreaListItem[]>([]);
    const [allAreas, setAllAreas] = useState<AreaListItem[]>([]);
    const [rootAreaId, setRootAreaId] = useState<number | null>(null);
    const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
    const [selectedParentAreaId, setSelectedParentAreaId] = useState<number | null>(null);
    const [spaceType, setSpaceType] = useState<SpaceType>('outdoor');
    const [geomMode, setGeomMode] = useState<GeomMode>('polygon');
    const [selectedSlamMapId, setSelectedSlamMapId] = useState<number | null>(null);

    // SLAM maps for picker
    const [availableSlamMaps, setAvailableSlamMaps] = useState<SlamMapOption[]>([]);
    const [slamMapsLoading, setSlamMapsLoading] = useState(false);

    // Parent area's SLAM map — used for IndoorAreaMap when creating child of indoor/mixed area
    const [parentSlamMapFull, setParentSlamMapFull] = useState<SlamMapData | null>(null);
    const [parentSlamMapLoading, setParentSlamMapLoading] = useState(false);
    const [indoorGeomResult, setIndoorGeomResult] = useState<IndoorGeomResult | null>(null);

    // Parent SLAM overlay data for Mapbox view (non-editable)
    const [parentSlamOverlayData, setParentSlamOverlayData] = useState<SlamOverlayData | null>(null);
    const [parentSlamPlacement, setParentSlamPlacement] = useState<SlamPlacement | null>(null);
    const parentSlamBlobUrlRef = useRef<string | null>(null);

    // Parent area's saved slamPlacement (fetched independently for IndoorAreaMap manualAnchor)
    const [parentAreaSlamPlacement, setParentAreaSlamPlacement] = useState<SlamPlacement | null>(null);

    // Selected SLAM map full data (for overlay on GPS map)
    const [selectedSlamMapFull, setSelectedSlamMapFull] = useState<SlamOverlayData | null>(null);
    const [selectedSlamCanvasMap, setSelectedSlamCanvasMap] = useState<SlamMapData | null>(null);
    const [slamPlacement, setSlamPlacement] = useState<SlamPlacement | null>(null);
    const [slamRotation, setSlamRotation] = useState<number>(0);

    // Map / UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedTypeHasTemplate, setSelectedTypeHasTemplate] = useState(false);
    const [viewState, setViewState] = useState(DEFAULT_VIEW);
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const searchTimeoutRef = useRef<any>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [templateImageUrl, setTemplateImageUrl] = useState<string | null>(null);
    const templateImageUrlRef = useRef<string | null>(null);
    const templateObjectUrl = useRef<string | null>(null);
    const markersRef = useRef<mapboxgl.Marker[]>([]);
    const markerOverlaysRef = useRef<mapboxgl.Marker[]>([]);
    const pointMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const autoAddingTemplateRef = useRef(false);
    const manualDrawBlockedRef = useRef(false);
    const [geomVersion, setGeomVersion] = useState(0);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [editAreaData, setEditAreaData] = useState<any>(null);
    const [pointCoords, setPointCoords] = useState<{ lng: number; lat: number } | null>(null);
    const [editStage, setEditStage] = useState<EditStage>('areaGeometry');
    const [anchorPointArmed, setAnchorPointArmed] = useState(false);
    const editStageInitKeyRef = useRef<string | null>(null);

    // ── Map view toggle (for mixed parent) ────────────────────────────────────
    const [useMapboxView, setUseMapboxView] = useState(false);

    // ── Transfer geometry: used to pass GPS-edited polygon back to IndoorAreaMap ──
    const [transferGeom, setTransferGeom] = useState<any>(null);

    // ── Editable polygon coordinate inputs ────────────────────────────────────
    const [coordsOverride, setCoordsOverride] = useState<{ lon: number; lat: number }[]>([]);
    const [generalMarkers, setGeneralMarkers] = useState<GeneralMarker[]>([]);
    const [missionWaypoints, setMissionWaypoints] = useState<MissionWaypointOverlay[]>([]);
    const [missionContextLoaded, setMissionContextLoaded] = useState(!resumeMissionId);
    const [companyRobots, setCompanyRobots] = useState<CompanyRobotOverlay[]>([]);
    const [markerDraftGpsCoords, setMarkerDraftGpsCoords] = useState<[number, number][]>([]);
    const [usedMarkerIds, setUsedMarkerIds] = useState<number[]>([]);
    const [markerFilterCornersOnly, setMarkerFilterCornersOnly] = useState(true);
    const [markersRefreshKey, setMarkersRefreshKey] = useState(0);
    const isEditingCoordsRef = useRef(false);
    const editTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const contextualFocusAppliedRef = useRef(false);

    const MAPBOX_TOKEN =
        process.env.REACT_APP_MAPBOX_TOKEN ||
        'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

    // ── Initial data load ──────────────────────────────────────────────────────

    useEffect(() => {
        const loadData = async () => {
            try {
                const [typesRes, areasRes] = await Promise.all([
                    axios.get(ENDPOINTS.AREA_TYPES_V2, { withCredentials: true }),
                    axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true }),
                ]);
                setAreaTypes(typesRes.data?.data || typesRes.data || []);
                const rawAreas = (areasRes.data?.data || areasRes.data || [])
                    .map((a: any) => ({
                        id: a.id,
                        name: a.name,
                        spaceType: a.spaceType,
                        parentAreaId: a.parentAreaId ?? null,
                        slamMapId: a.slamMapId ?? null,
                    }));
                setAllAreas(rawAreas);
                const rootArea = rawAreas.find((a: AreaListItem) => a.spaceType === 'root_location');
                if (rootArea?.id) setRootAreaId(rootArea.id);
                setAreas(rawAreas
                    .filter((a: any) => a.spaceType !== 'root_location')
                    .map((a: any) => ({ id: a.id, name: a.name, spaceType: a.spaceType, parentAreaId: a.parentAreaId ?? null, slamMapId: a.slamMapId ?? null })));

                if (areaIdFromUrl) {
                    const areaRes = await axios.get(ENDPOINTS.AREA_DETAIL(parseInt(areaIdFromUrl)), {
                        withCredentials: true
                    });
                    const areaData = areaRes.data?.data || areaRes.data;
                    setEditAreaData(areaData);
                    // Set space type from existing area
                    if (areaData?.spaceType) setSpaceType(areaData.spaceType as SpaceType);
                    if (areaData?.slamMapId) setSelectedSlamMapId(areaData.slamMapId);
                } else if (parentIdFromUrl) {
                    const parentId = parseInt(parentIdFromUrl);
                    form.setFieldsValue({ parentAreaId: parentId });
                    setSelectedParentAreaId(parentId);
                } else {
                    const companyId = currentUser?.currentCompanyId || currentUser?.companyId;
                    if (companyId) {
                        try {
                            const compRes = await axios.get(ENDPOINTS.GET_COMPANIES + `/${companyId}`, { withCredentials: true });
                            const rootId = compRes.data?.rootAreaId;
                            if (rootId) {
                                setRootAreaId(rootId);
                                const areaRes = await axios.get(`${ENDPOINTS.AREAS_V2}/${rootId}`, { withCredentials: true });
                                const rootArea = areaRes.data?.data || areaRes.data;
                                if (rootArea?.geom?.type === 'Point' && Array.isArray(rootArea.geom.coordinates)) {
                                    const [lng, lat] = rootArea.geom.coordinates;
                                    setViewState((prev) => ({ ...prev, longitude: lng, latitude: lat, zoom: 15 }));
                                }
                            }
                        } catch { /* non-critical */ }
                    }
                }
            } catch (error) {
                console.error('Failed to load area types/areas', error);
                message.error('Unable to load area setup data.');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [areaIdFromUrl, parentIdFromUrl]);

    // Mission context is loaded from the persisted draft so large waypoint sets
    // do not need to be encoded into the area-creation URL.
    useEffect(() => {
        if (!resumeMissionId) {
            setMissionWaypoints([]);
            setMissionContextLoaded(true);
            return;
        }

        let cancelled = false;
        setMissionContextLoaded(false);
        axios.get(ENDPOINTS.MISSION_DETAIL(resumeMissionId), { withCredentials: true })
            .then((response) => {
                if (cancelled) return;
                const mission = response.data?.data || response.data || {};
                const points = (Array.isArray(mission.missionPoints) ? mission.missionPoints : [])
                    .map((point: any, index: number): MissionWaypointOverlay | null => {
                        const location = gpsPointFromValue(point);
                        if (!location) return null;
                        return {
                            id: point.id ?? `mission-waypoint-${index + 1}`,
                            label: point.label || `Waypoint ${index + 1}`,
                            ...location,
                        };
                    })
                    .filter((point: MissionWaypointOverlay | null): point is MissionWaypointOverlay => Boolean(point));
                setMissionWaypoints(points);
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to load mission waypoints for area creation', error);
                    setMissionWaypoints([]);
                    message.warning('The mission waypoints could not be shown on the area map.');
                }
            })
            .finally(() => {
                if (!cancelled) setMissionContextLoaded(true);
            });

        return () => { cancelled = true; };
    }, [resumeMissionId]);

    // Keep the latest GPS location of every robot assigned to the active company
    // visible while an area is being created or edited.
    useEffect(() => {
        if (!currentCompanyId) {
            setCompanyRobots([]);
            return;
        }

        let cancelled = false;
        const loadRobotLocations = async () => {
            try {
                const response = await axios.get(ENDPOINTS.GET_DEVICES, {
                    withCredentials: true,
                    params: {
                        assignment_status: 'mine',
                        company_id: currentCompanyId,
                    },
                });
                if (cancelled) return;
                const devices = response.data?.data || response.data || [];
                const locations = (Array.isArray(devices) ? devices : [])
                    .map((device: any): CompanyRobotOverlay | null => {
                        const location = gpsPointFromValue(device);
                        if (!location) return null;
                        return {
                            id: Number(device.id),
                            label: device.label || `Robot ${device.id}`,
                            status: device.currentStatus || null,
                            lastHeartbeat: device.lastHeartbeat || null,
                            ...location,
                        };
                    })
                    .filter((robot: CompanyRobotOverlay | null): robot is CompanyRobotOverlay => (
                        Boolean(robot) && Number.isInteger(robot?.id) && Number(robot?.id) > 0
                    ));
                setCompanyRobots(locations);
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load company robot locations for area creation', error);
                    setCompanyRobots([]);
                }
            }
        };

        void loadRobotLocations();
        const intervalId = window.setInterval(() => void loadRobotLocations(), 10000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [currentCompanyId]);

    // ── Fetch SLAM maps for picker ─────────────────────────────────────────────

    useEffect(() => {
        if (spaceType === 'outdoor') {
            setAvailableSlamMaps([]);
            setSelectedSlamMapId(null);
            return;
        }
        let cancelled = false;
        setSlamMapsLoading(true);
        setAvailableSlamMaps([]);

        const fetchSlamMaps = async () => {
            try {
                let res;
                if (selectedParentAreaId) {
                    // Fetch maps for the parent area (assigned + orphaned)
                    res = await axios.get(ENDPOINTS.AREA_SLAM_MAPS(selectedParentAreaId), { withCredentials: true });
                } else {
                    // No parent — fetch all company-wide maps
                    res = await axios.get(ENDPOINTS.SLAM_MAPS, { withCredentials: true });
                }
                if (!cancelled) {
                    const rawMaps = res.data?.data || [];
                    console.log('[AreaCreate] fetchSlamMaps response:', rawMaps.map((m: any) => ({
                        id: m.id, name: m.name,
                        imageStoragePath: m.imageStoragePath,
                        imageUrl: m.imageUrl,
                        mapType: m.mapType,
                    })));
                    setAvailableSlamMaps(rawMaps);
                }
            } catch (err: any) {
                console.error('[AreaCreate] fetchSlamMaps error:', err?.response?.data || err?.message);
                if (!cancelled) setAvailableSlamMaps([]);
            } finally {
                if (!cancelled) setSlamMapsLoading(false);
            }
        };

        fetchSlamMaps();
        return () => { cancelled = true; };
    }, [spaceType, selectedParentAreaId]);

    // ── Markers ────────────────────────────────────────────────────────────────

    const updateMarkers = useCallback((coordList: any[]) => {
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];
        const map = mapRef.current?.getMap();
        if (!map) return;
        const trimmed =
            coordList.length > 1 &&
            coordList[0][0] === coordList[coordList.length - 1][0] &&
            coordList[0][1] === coordList[coordList.length - 1][1]
                ? coordList.slice(0, -1)
                : coordList;
        trimmed.forEach((c: any, idx: number) => {
            if (!c || c[0] === undefined || c[1] === undefined || c[0] === null || c[1] === null) return;
            const el = document.createElement('div');
            el.className = 'area-vertex-label';
            el.innerText = String(idx + 1);
            const marker = new mapboxgl.Marker({ element: el }).setLngLat([c[0], c[1]]).addTo(map);
            markersRef.current.push(marker);
        });
    }, []);

    // ── Template overlay ──────────────────────────────────────────────────────

    useEffect(() => {
        templateImageUrlRef.current = templateImageUrl;
    }, [templateImageUrl]);

    const updateTemplateOverlay = useCallback(
        (coordList: any[] = []) => {
            const currentImageUrl = templateImageUrlRef.current;
            const map = mapRef.current?.getMap?.();
            if (!map) return;
            const hasImage = !!currentImageUrl;
            const usableCoords = coordList.length >= 4 ? coordList.slice(0, 4) : [];
            if (!hasImage || usableCoords.length < 4) {
                if (map.getLayer('template-image-layer')) map.removeLayer('template-image-layer');
                if (map.getSource('template-image')) map.removeSource('template-image');
                return;
            }
            const bounds = [
                [usableCoords[3][0], usableCoords[3][1]],
                [usableCoords[2][0], usableCoords[2][1]],
                [usableCoords[1][0], usableCoords[1][1]],
                [usableCoords[0][0], usableCoords[0][1]]
            ];
            if (map.getLayer('template-image-layer')) map.removeLayer('template-image-layer');
            if (map.getSource('template-image')) map.removeSource('template-image');
            map.addSource('template-image', { type: 'image', url: currentImageUrl!, coordinates: bounds as any });
            map.addLayer({
                id: 'template-image-layer',
                type: 'raster',
                source: 'template-image',
                paint: { 'raster-opacity': 0.5 },
            });
        },
        [],
    );

    const refreshFromDraw = useCallback(() => {
        const draw = drawRef.current;
        if (!draw) return;
        try {
            const all = draw.getAll();
            const poly = all.features.find((f: any) => f.geometry?.type === 'Polygon');
            const coords = poly?.geometry?.coordinates?.[0] || [];
            updateMarkers(coords);
            updateTemplateOverlay(coords);
            setGeomVersion((v) => v + 1);
        } catch { /* draw's internal map context may be destroyed after view switch */ }
    }, [updateMarkers, updateTemplateOverlay]);

    // ── Point mode management ─────────────────────────────────────────────────

    const clearPointMarker = useCallback(() => {
        if (pointMarkerRef.current) {
            pointMarkerRef.current.remove();
            pointMarkerRef.current = null;
        }
        setPointCoords(null);
    }, []);

    const placePointMarker = useCallback((lng: number, lat: number) => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        if (pointMarkerRef.current) pointMarkerRef.current.remove();
        const el = document.createElement('div');
        el.style.cssText = 'position:relative;width:40px;height:40px;';
        // Pulsing ring
        const pulse = document.createElement('div');
        pulse.style.cssText = 'position:absolute;top:0;left:0;width:40px;height:40px;border-radius:50%;background:rgba(22,119,255,0.25);animation:area-pin-pulse 1.5s ease-out infinite;';
        el.appendChild(pulse);
        // Inner circle
        const dot = document.createElement('div');
        dot.style.cssText = 'position:absolute;top:8px;left:8px;width:24px;height:24px;background:#1677ff;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.4);';
        el.appendChild(dot);
        // Center icon
        const icon = document.createElement('div');
        icon.style.cssText = 'position:absolute;top:12px;left:12px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:12px;font-weight:bold;pointer-events:none;';
        icon.innerHTML = '📍';
        el.appendChild(icon);
        // Label
        const label = document.createElement('div');
        label.style.cssText = 'position:absolute;top:-22px;left:50%;transform:translateX(-50%);white-space:nowrap;background:rgba(0,0,0,0.75);color:#fff;padding:2px 8px;border-radius:8px;font-size:10px;pointer-events:none;';
        label.textContent = 'Area location';
        el.appendChild(label);
        // Inject pulse animation if not already
        if (!document.getElementById('area-pin-pulse-style')) {
            const style = document.createElement('style');
            style.id = 'area-pin-pulse-style';
            style.textContent = '@keyframes area-pin-pulse{0%{transform:scale(0.8);opacity:1}100%{transform:scale(2);opacity:0}}';
            document.head.appendChild(style);
        }
        pointMarkerRef.current = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
            draggable: editStage === 'anchorPoint',
        }).setLngLat([lng, lat]).addTo(map);
        pointMarkerRef.current.on('dragend', () => {
            const ll = pointMarkerRef.current?.getLngLat();
            if (ll) setPointCoords({ lng: ll.lng, lat: ll.lat });
        });
        setPointCoords({ lng, lat });
    }, [editStage]);

    useEffect(() => {
        pointMarkerRef.current?.setDraggable(editStage === 'anchorPoint');
    }, [editStage]);

    // Register/remove click listener for point/anchor mode
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !mapLoaded) return;

        // Remove any existing listener
        if (clickListenerRef.current) {
            map.off('click', clickListenerRef.current);
            clickListenerRef.current = null;
        }

        const canPlacePoint = editStage === 'anchorPoint' ? anchorPointArmed : geomMode === 'point';

        if (canPlacePoint) {
            const listener = (e: any) => {
                placePointMarker(e.lngLat.lng, e.lngLat.lat);
                if (editStage === 'anchorPoint') setAnchorPointArmed(false);
            };
            map.on('click', listener);
            clickListenerRef.current = listener;
            // Hide draw controls in point mode
            const container = map.getContainer();
            container.querySelectorAll('.mapbox-gl-draw_ctrl-draw-btn, .mapbox-gl-draw_ctrl-icon-trash, .mapbox-gl-draw_trash')
                .forEach((btn) => { (btn as HTMLElement).style.display = 'none'; });
        } else {
            // Show/hide draw controls based on template
            const container = map.getContainer();
            const showControls = !selectedTypeHasTemplate;
            container.querySelectorAll('.mapbox-gl-draw_ctrl-draw-btn, .mapbox-gl-draw_ctrl-icon-trash, .mapbox-gl-draw_trash')
                .forEach((btn) => { (btn as HTMLElement).style.display = showControls ? '' : 'none'; });
        }
    }, [geomMode, mapLoaded, placePointMarker, selectedTypeHasTemplate, editStage, anchorPointArmed]);

    // ── Edit mode population ──────────────────────────────────────────────────

    useEffect(() => {
        if (!editAreaData) return;

        form.setFieldsValue({
            name: editAreaData.name,
            code: editAreaData.code || '',
            areaTypeId: editAreaData.areaType?.id,
            parentAreaId: editAreaData.parentAreaId || undefined,
        });

        setNameValue(editAreaData.name);
        setSelectedTypeId(editAreaData.areaType?.id || null);
        setSelectedParentAreaId(editAreaData.parentAreaId || null);
        if (editAreaData.spaceType) setSpaceType(editAreaData.spaceType as SpaceType);
        if (editAreaData.slamMapId) setSelectedSlamMapId(editAreaData.slamMapId);
    }, [editAreaData, form]);

    useEffect(() => {
        if (!editAreaData || !mapLoaded) return;

        const draw = drawRef.current;
        if (!editAreaData.geom) return;
        if (isIndoorLikeSpaceType(spaceType) && useMapboxView) return;

        if (editAreaData.geom.type === 'Point') {
            setGeomMode('point');
            const [lng, lat] = editAreaData.geom.coordinates;
            placePointMarker(lng, lat);
            mapRef.current?.flyTo({ center: [lng, lat], zoom: 15 });
        } else if (draw) {
            draw.deleteAll();
            draw.add({
                type: 'Feature',
                properties: {},
                geometry: editAreaData.geom
            } as any);
            refreshFromDraw();
            const coords = editAreaData.geom.coordinates[0];
            if (coords && coords.length > 0) {
                const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                mapRef.current?.flyTo({ center: [lon, lat], zoom: 15 });
            }
        }
    }, [editAreaData, mapLoaded, refreshFromDraw, placePointMarker]);

    // ── Map load ──────────────────────────────────────────────────────────────

    const onMapLoad = useCallback(() => {
        if (!mapRef.current) return;
        const map = mapRef.current.getMap();
        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
        });
        drawRef.current = draw;
        map.addControl(draw, 'top-left');

        const tagDrawControlButtons = () => {
            const container = map.getContainer();
            const polygonButton = container.querySelector('.mapbox-gl-draw_polygon');
            if (polygonButton instanceof HTMLElement) polygonButton.classList.add('area-create-draw-polygon-button');
            const trashButton = container.querySelector('.mapbox-gl-draw_trash, .mapbox-gl-draw_ctrl-icon-trash');
            if (trashButton instanceof HTMLElement) trashButton.classList.add('area-create-draw-trash-button');
        };
        tagDrawControlButtons();
        window.setTimeout(tagDrawControlButtons, 0);

        map.on('draw.create', (e: any) => {
            if (manualDrawBlockedRef.current && !autoAddingTemplateRef.current) {
                draw.deleteAll();
                return;
            }
            const all = draw.getAll();
            if (all.features.length > 1) {
                const newFeatureId = e.features[0].id;
                all.features.forEach((f: any) => {
                    if (f.id !== newFeatureId) draw.delete(f.id);
                });
            }
            refreshFromDraw();
        });

        map.on('draw.update', refreshFromDraw);
        map.on('draw.delete', refreshFromDraw);
        map.on('draw.render', refreshFromDraw);

        setMapLoaded(true);
    }, [refreshFromDraw]);

    // ── Type helpers ──────────────────────────────────────────────────────────

    const selectedType = useMemo(() => areaTypes.find((t) => t.id === selectedTypeId) || null, [areaTypes, selectedTypeId]);
    const maxVertices = selectedType?.maxVertices || null;

    // ── Geocoding ─────────────────────────────────────────────────────────────

    const handleSearch = async (query: string) => {
        if (!query) { setSearchResults([]); return; }
        setSearching(true);
        try {
            const res = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5`,
            );
            const data = await res.json();
            setSearchResults((data?.features || []).map((f: any) => ({
                id: f.id, name: f.text, center: f.center, place_name: f.place_name,
            })));
        } catch {
            message.error('Unable to search location.');
        } finally {
            setSearching(false);
        }
    };

    const debouncedSearch = (value: string) => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => handleSearch(value), 300);
    };

    const selectSearchResult = (result: SearchResult) => {
        setSearchResults([]);
        setSearchQuery(result.place_name);
        const [lng, lat] = result.center;
        setViewState((prev) => ({ ...prev, longitude: lng, latitude: lat, zoom: 15 }));
        mapRef.current?.flyTo({ center: [lng, lat], zoom: 15 });
    };

    // ── Template shape ────────────────────────────────────────────────────────

    const addTemplateShape = useCallback(() => {
        const draw = drawRef.current;
        const map = mapRef.current?.getMap();
        if (!draw || !map || !selectedType || isEditMode) return;

        const widthMeters = selectedType.templateDimensions?.widthMeters || 100;
        const heightMeters = selectedType.templateDimensions?.heightMeters || 50;

        const center = map.getCenter();
        const metersToLng = (m: number, lat: number) => m / (111320 * Math.cos((lat * Math.PI) / 180));
        const metersToLat = (m: number) => m / 111320;
        const halfWidthLng = metersToLng(widthMeters / 2, center.lat);
        const halfHeightLat = metersToLat(heightMeters / 2);

        const coords: [number, number][] = [
            [center.lng - halfWidthLng, center.lat - halfHeightLat],
            [center.lng + halfWidthLng, center.lat - halfHeightLat],
            [center.lng + halfWidthLng, center.lat + halfHeightLat],
            [center.lng - halfWidthLng, center.lat + halfHeightLat],
            [center.lng - halfWidthLng, center.lat - halfHeightLat],
        ];

        autoAddingTemplateRef.current = true;
        (draw as any).deleteAll?.();
        draw.add({
            type: 'Feature', properties: {},
            geometry: { type: 'Polygon', coordinates: [coords] },
        } as any);
        setTimeout(() => { autoAddingTemplateRef.current = false; }, 0);
        updateMarkers(coords);
        updateTemplateOverlay(coords);
        setGeomVersion((v) => v + 1);
    }, [isEditMode, selectedType, updateMarkers, updateTemplateOverlay]);

    const clearTemplateShape = useCallback(() => {
        const draw = drawRef.current;
        if (!draw) return;
        try { draw.deleteAll(); } catch { /* draw context may be stale after view switch */ }
        refreshFromDraw();
    }, [refreshFromDraw]);

    // ── Editable coordinate callbacks ─────────────────────────────────────────

    const updatePolygonFromCoords = useCallback((newCoords: { lon: number; lat: number }[]) => {
        const draw = drawRef.current;
        if (!draw || newCoords.length < 3) return;
        const ring: [number, number][] = [
            ...newCoords.map(c => [c.lon, c.lat] as [number, number]),
            [newCoords[0].lon, newCoords[0].lat],
        ];
        autoAddingTemplateRef.current = true;
        draw.deleteAll();
        draw.add({
            type: 'Feature', properties: {},
            geometry: { type: 'Polygon', coordinates: [ring] },
        } as any);
        setTimeout(() => { autoAddingTemplateRef.current = false; }, 0);
        refreshFromDraw();
    }, [refreshFromDraw]);

    const handleCoordChange = useCallback((idx: number, field: 'lon' | 'lat', value: number | null) => {
        if (value === null || value === undefined) return;
        isEditingCoordsRef.current = true;
        if (editTimerRef.current) clearTimeout(editTimerRef.current);
        editTimerRef.current = setTimeout(() => { isEditingCoordsRef.current = false; }, 800);
        setCoordsOverride(prev => {
            const next = prev.map((p, i) => i === idx ? { ...p, [field]: value } : p);
            updatePolygonFromCoords(next);
            return next;
        });
    }, [updatePolygonFromCoords]);

    /** Handle coordinate edits from the indoor polygon coordinates card (meters) */
    const handleIndoorCoordChange = useCallback((idx: number, field: 'x' | 'y', value: number | null) => {
        if (value === null || value === undefined) return;
        if (!indoorGeomResult?.localGeom) return;
        const geom = indoorGeomResult.localGeom;
        if (geom.type !== 'Polygon' || !geom.coordinates?.[0]) return;
        const ring: [number, number][] = [...geom.coordinates[0]];
        // ring has closing duplicate, so work on ring[idx]
        const isClosed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
        const pts = isClosed ? ring.slice(0, -1) : [...ring];
        if (idx >= pts.length) return;
        pts[idx] = field === 'x' ? [value, pts[idx][1]] : [pts[idx][0], value];
        const newRing = [...pts, pts[0]]; // close ring
        const newLocalGeom = { type: 'Polygon', coordinates: [newRing] };
        // Push new geometry into IndoorAreaMap via transferGeom
        setTransferGeom(newLocalGeom);
    }, [indoorGeomResult]);

    /** Handle GPS coordinate edits from the polygon coordinates card (for mixed parent indoor maps).
     *  Converts edited GPS coordinates back to local metres and pushes via transferGeom. */
    const handleIndoorGpsCoordChange = useCallback((idx: number, field: 'lon' | 'lat', value: number | null) => {
        if (value === null || value === undefined) return;
        const selectedGeoAnchor = selectedSlamCanvasMap?.geoAnchor;
        const savedPlacement = editAreaData?.configurations?.slamPlacement;
        const anchor = parentAreaSlamPlacement
            || (savedPlacement?.lat != null && savedPlacement?.lng != null
                ? {
                    lat: Number(savedPlacement.lat),
                    lng: Number(savedPlacement.lng),
                    bearingDegrees: Number(savedPlacement.bearingDegrees ?? 0),
                }
                : null)
            || (selectedGeoAnchor
                ? {
                    lat: selectedGeoAnchor.lat,
                    lng: selectedGeoAnchor.lng,
                    bearingDegrees: selectedGeoAnchor.bearingDegrees,
                }
                : null);
        if (!indoorGeomResult?.gpsGeom || !anchor) return;
        const geom = indoorGeomResult.gpsGeom;
        if (geom.type !== 'Polygon' || !geom.coordinates?.[0]) return;
        const ring: [number, number][] = [...geom.coordinates[0]]; // [lng, lat]
        const isClosed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
        const pts = isClosed ? ring.slice(0, -1) : [...ring];
        if (idx >= pts.length) return;
        // Update the edited GPS coordinate
        pts[idx] = field === 'lon' ? [value, pts[idx][1]] : [pts[idx][0], value];
        // Convert all GPS points back to local metres
        const localPts: [number, number][] = pts.map(([lng, lat]) =>
            gpsToLocalMeters(lat, lng, anchor.lat, anchor.lng, anchor.bearingDegrees));
        const localRing = [...localPts, localPts[0]]; // close ring
        const newLocalGeom = { type: 'Polygon', coordinates: [localRing] };
        setTransferGeom(newLocalGeom);
    }, [indoorGeomResult, parentAreaSlamPlacement, selectedSlamCanvasMap, editAreaData]);

    // ── Derived state computed once (needed by handleSave before render) ───────

    const parentAreaInfoDerived = areas.find(a => a.id === selectedParentAreaId);
    const parentIsIndoor = parentAreaInfoDerived?.spaceType === 'indoor';
    const parentIsMixed = parentAreaInfoDerived?.spaceType === 'mixed';
    const parentIsIndoorLikeDerived = parentIsIndoor || parentIsMixed;
    const hasParentSlamMap = parentIsIndoorLikeDerived && !!parentSlamMapFull?.imageUrl;
    const areaOwnsSlamCanvas = isIndoorLikeSpaceType(spaceType) && !hasParentSlamMap && !!selectedSlamCanvasMap?.imageUrl;
    const activeSlamCanvas = hasParentSlamMap ? parentSlamMapFull : (areaOwnsSlamCanvas ? selectedSlamCanvasMap : null);
    const activeSlamCanvasIsParent = !!(hasParentSlamMap && parentSlamMapFull);
    const activeSlamCanvasIsOwn = !!(areaOwnsSlamCanvas && selectedSlamCanvasMap);
    const activeSlamCanvasIsMixed = activeSlamCanvasIsParent ? !!parentIsMixed : (activeSlamCanvasIsOwn && spaceType === 'mixed');
    const editAreaSavedPlacement = editAreaData?.configurations?.slamPlacement;
    const ownAreaSlamPlacement: SlamPlacement | null = editAreaSavedPlacement?.lat != null && editAreaSavedPlacement?.lng != null
        ? {
            lat: Number(editAreaSavedPlacement.lat),
            lng: Number(editAreaSavedPlacement.lng),
            bearingDegrees: Number(editAreaSavedPlacement.bearingDegrees ?? 0),
        }
        : null;
    const activeSlamManualAnchor = activeSlamCanvasIsParent
        ? (parentIsMixed ? parentAreaSlamPlacement : null)
        : (activeSlamCanvasIsOwn && spaceType === 'mixed' ? (slamPlacement || ownAreaSlamPlacement) : null);
    const activeSlamMapboxAnchor = activeSlamCanvasIsParent
        ? (parentAreaSlamPlacement || null)
        : (activeSlamCanvasIsOwn ? (slamPlacement || ownAreaSlamPlacement) : null);
    const activeSlamCanShowMapbox = !!(activeSlamCanvas && (activeSlamCanvas.geoAnchor || activeSlamMapboxAnchor || selectedSlamMapFull));
    const activeSlamCanEmitGps = !!(activeSlamCanvas?.geoAnchor || activeSlamManualAnchor);
    const ownSlamCanvasLoading = isEditMode && isIndoorLikeSpaceType(spaceType) && !!selectedSlamMapId && !hasParentSlamMap && !selectedSlamCanvasMap;
    // When an active SLAM canvas can be represented in GPS space, the user can toggle to Mapbox view.
    const showIndoorMap = !!activeSlamCanvas && !(activeSlamCanShowMapbox && useMapboxView);
    const selectedSlamIsUnanchored = !!(selectedSlamMapFull && !selectedSlamMapFull.geoAnchor);
    const isStagedMixedFlow = isIndoorLikeSpaceType(spaceType) && selectedSlamIsUnanchored && !!slamPlacement;
    const canEditSlamPlacement = isStagedMixedFlow && editStage === 'slamPlacement';
    const canEditAnchorPoint = isStagedMixedFlow && editStage === 'anchorPoint';
    const canEditAreaGeometry = !isStagedMixedFlow || editStage === 'areaGeometry';

    useEffect(() => {
        if (
            !mapLoaded
            || showIndoorMap
            || isEditMode
            || contextualFocusAppliedRef.current
            || (resumeMissionId && !missionContextLoaded)
        ) {
            return;
        }

        const locations = missionWaypoints.length > 0 ? missionWaypoints : companyRobots;
        if (locations.length === 0) return;

        const map = mapRef.current?.getMap();
        if (!map) return;

        contextualFocusAppliedRef.current = true;
        if (locations.length === 1) {
            map.flyTo({ center: [locations[0].lng, locations[0].lat], zoom: 16 });
            return;
        }

        const bounds = locations.reduce(
            (nextBounds, point) => nextBounds.extend([point.lng, point.lat]),
            new mapboxgl.LngLatBounds(
                [locations[0].lng, locations[0].lat],
                [locations[0].lng, locations[0].lat]
            )
        );
        map.fitBounds(bounds, { padding: 80, maxZoom: 17 });
    }, [
        companyRobots,
        isEditMode,
        mapLoaded,
        missionContextLoaded,
        missionWaypoints,
        resumeMissionId,
        showIndoorMap,
    ]);

    const markerBranchAreaIds = useMemo(() => {
        const branch: number[] = [];
        const byId = new globalThis.Map<number, AreaListItem>(allAreas.map((area) => [area.id, area]));
        let cursor = selectedParentAreaId || editAreaData?.parentAreaId || rootAreaId || null;
        const seen = new Set<number>();
        while (cursor && !seen.has(cursor)) {
            seen.add(cursor);
            branch.push(cursor);
            cursor = byId.get(cursor)?.parentAreaId || null;
        }
        if (rootAreaId && !seen.has(rootAreaId)) branch.push(rootAreaId);
        return branch;
    }, [allAreas, selectedParentAreaId, editAreaData?.parentAreaId, rootAreaId]);
    const markerSlamMapContextId = activeSlamCanvas?.id || selectedSlamMapId || null;

    const indoorMapMarkers = useMemo<IndoorMapMarker[]>(() => (
        generalMarkers
            .filter((marker) => {
                const x = Number(marker.localPose?.x);
                const y = Number(marker.localPose?.y);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
                if (markerSlamMapContextId && marker.slamMapId && marker.slamMapId !== markerSlamMapContextId) return false;
                return true;
            })
            .map((marker) => ({
                id: marker.id,
                label: marker.label || marker.category?.name || marker.markerType || `Marker ${marker.id}`,
                categoryKey: marker.category?.key || marker.markerType || null,
                localPose: marker.localPose ? {
                    ...marker.localPose,
                    x: Number(marker.localPose.x),
                    y: Number(marker.localPose.y),
                } : null,
            }))
    ), [generalMarkers, markerSlamMapContextId]);

    const handleIndoorMarkerSelect = useCallback((marker: IndoorMapMarker) => {
        if (marker.categoryKey && marker.categoryKey !== 'area_corner') {
            message.info('Marker selected');
            return;
        }
        const markerId = Number(marker.id);
        if (Number.isFinite(markerId)) {
            setUsedMarkerIds((prev) => prev.includes(markerId) ? prev : [...prev, markerId]);
        }
        message.success(geomMode === 'point' ? 'Marker used as area location' : 'Marker added as an area corner');
    }, [geomMode]);

    const appendGpsMarkerToGeometry = useCallback((marker: GeneralMarker) => {
        const coords = marker.geom?.type === 'Point' && Array.isArray(marker.geom.coordinates)
            ? marker.geom.coordinates
            : null;
        const lng = Number(coords?.[0]);
        const lat = Number(coords?.[1]);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

        if (geomMode === 'point') {
            placePointMarker(lng, lat);
            setUsedMarkerIds((prev) => prev.includes(marker.id) ? prev : [...prev, marker.id]);
            message.success('Marker used as area location');
            return;
        }

        if ((marker.category?.key || marker.markerType) !== 'area_corner') {
            message.info('Only area corner markers are added as polygon vertices');
            return;
        }

        const draw = drawRef.current;
        const existing = draw?.getAll?.().features?.find((f: any) => f.geometry?.type === 'Polygon');
        const existingCoords = existing?.geometry?.coordinates?.[0] || [];
        const openExisting = existingCoords.length > 1
            && existingCoords[0][0] === existingCoords[existingCoords.length - 1][0]
            && existingCoords[0][1] === existingCoords[existingCoords.length - 1][1]
            ? existingCoords.slice(0, -1)
            : existingCoords;
        const baseCoords = openExisting.length > 0 ? openExisting : markerDraftGpsCoords;
        const nextCoords: [number, number][] = [...baseCoords, [lng, lat]];
        if (maxVertices && nextCoords.length > maxVertices) {
            message.error(`This area type allows up to ${maxVertices} vertices.`);
            return;
        }
        setUsedMarkerIds((prev) => prev.includes(marker.id) ? prev : [...prev, marker.id]);
        setMarkerDraftGpsCoords(nextCoords);
        if (!draw || nextCoords.length < 3) {
            message.info(`${nextCoords.length} marker corner${nextCoords.length === 1 ? '' : 's'} selected; choose ${3 - nextCoords.length} more.`);
            return;
        }
        draw.deleteAll();
        draw.add({
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: [[...nextCoords, nextCoords[0]]],
            },
        } as any);
        refreshFromDraw();
        message.success('Marker added as an area corner');
    }, [geomMode, markerDraftGpsCoords, maxVertices, placePointMarker, refreshFromDraw]);

    useEffect(() => {
        // Markers are always scoped to the parent-area branch (selected parent →
        // ... → root). Robot corner captures without an explicit area are linked
        // to the company root area, which is always part of the branch.
        const shouldLoad = Boolean(markerBranchAreaIds.length || markerSlamMapContextId);
        if (!shouldLoad) {
            setGeneralMarkers([]);
            return;
        }
        let cancelled = false;
        const params: Record<string, any> = {};
        if (markerBranchAreaIds.length) params.areaIds = markerBranchAreaIds.join(',');
        if (spaceType !== 'outdoor' && markerSlamMapContextId) params.slamMapId = markerSlamMapContextId;
        axios.get(ENDPOINTS.MARKERS, { params, withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                setGeneralMarkers(Array.isArray(res.data?.data) ? res.data.data : []);
            })
            .catch((error) => {
                if (!cancelled) {
                    console.warn('Failed to load markers', error);
                    setGeneralMarkers([]);
                }
            });
        return () => { cancelled = true; };
    }, [markerBranchAreaIds, markerSlamMapContextId, spaceType, markersRefreshKey]);

    const gpsMarkers = useMemo(() => (
        generalMarkers.filter((marker) => {
            const coords = marker.geom?.type === 'Point' && Array.isArray(marker.geom.coordinates)
                ? marker.geom.coordinates
                : null;
            if (!Number.isFinite(Number(coords?.[0])) || !Number.isFinite(Number(coords?.[1]))) return false;
            if (!markerFilterCornersOnly) return true;
            return (marker.category?.key || marker.markerType) === 'area_corner';
        })
    ), [generalMarkers, markerFilterCornersOnly]);

    useEffect(() => {
        markerOverlaysRef.current.forEach((marker) => marker.remove());
        markerOverlaysRef.current = [];
        const map = mapRef.current?.getMap();
        if (!map || !mapLoaded || showIndoorMap) return;

        gpsMarkers.forEach((marker) => {
            const coords = marker.geom?.type === 'Point' && Array.isArray(marker.geom.coordinates)
                ? marker.geom.coordinates
                : null;
            const lng = Number(coords?.[0]);
            const lat = Number(coords?.[1]);
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
            const categoryKey = marker.category?.key || marker.markerType || 'poi';
            const isUsed = usedMarkerIds.includes(marker.id);
            const el = document.createElement('button');
            el.type = 'button';
            el.title = `${marker.label || marker.category?.name || 'Marker'}${isUsed ? ' (added)' : ''}`;
            el.style.cssText = [
                'width:18px',
                'height:18px',
                'border-radius:50%',
                'border:2px solid #fff',
                `background:${isUsed ? '#8c8c8c' : categoryKey === 'area_corner' ? '#1677ff' : categoryKey === 'hazard' ? '#ff4d4f' : '#13c2c2'}`,
                'box-shadow:0 1px 5px rgba(0,0,0,0.45)',
                'cursor:pointer',
                'padding:0',
                isUsed ? 'opacity:0.65' : '',
            ].filter(Boolean).join(';');
            el.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                appendGpsMarkerToGeometry(marker);
            });
            markerOverlaysRef.current.push(new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat([lng, lat]).addTo(map));
        });
        return () => {
            markerOverlaysRef.current.forEach((marker) => marker.remove());
            markerOverlaysRef.current = [];
        };
    }, [appendGpsMarkerToGeometry, gpsMarkers, mapLoaded, showIndoorMap, usedMarkerIds]);

    useEffect(() => {
        if (!isIndoorLikeSpaceType(spaceType) || !selectedSlamMapFull || selectedSlamMapFull.geoAnchor) {
            editStageInitKeyRef.current = null;
            setEditStage('areaGeometry');
            setAnchorPointArmed(false);
            return;
        }
        if (!slamPlacement) return;
        const savedPlacement = editAreaData?.configurations?.slamPlacement;
        const initKey = `${spaceType}:${selectedSlamMapId ?? 'none'}:${savedPlacement ? 'saved' : 'new'}`;
        if (editStageInitKeyRef.current === initKey) return;
        editStageInitKeyRef.current = initKey;
        setEditStage(savedPlacement ? 'areaGeometry' : 'slamPlacement');
        setAnchorPointArmed(false);
    }, [spaceType, selectedSlamMapFull, selectedSlamMapId, slamPlacement, editAreaData?.configurations?.slamPlacement]);

    useEffect(() => {
        if (editStage === 'slamPlacement' || editStage === 'anchorPoint') {
            setUseMapboxView(true);
        }
    }, [editStage]);

    useEffect(() => {
        manualDrawBlockedRef.current = !canEditAreaGeometry || !!(selectedTypeId && selectedTypeHasTemplate && !isEditMode);
    }, [canEditAreaGeometry, selectedTypeId, selectedTypeHasTemplate, isEditMode]);

    useEffect(() => {
        if (!mapLoaded || showIndoorMap) return;
        const map = mapRef.current?.getMap();
        if (!map) return;

        const visibility = canEditAreaGeometry ? 'visible' : 'none';
        map.getStyle()?.layers?.forEach((layer: any) => {
            if (typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')) {
                try { map.setLayoutProperty(layer.id, 'visibility', visibility); } catch { /* layer may be gone */ }
            }
        });
        try {
            if (map.getLayer('template-image-layer')) {
                map.setLayoutProperty('template-image-layer', 'visibility', visibility);
            }
        } catch { /* layer may be gone */ }

        if (!canEditAreaGeometry) {
            markersRef.current.forEach((marker) => marker.remove());
            markersRef.current = [];
        } else {
            refreshFromDraw();
        }
    }, [mapLoaded, showIndoorMap, canEditAreaGeometry, refreshFromDraw]);

    useEffect(() => {
        if (!isEditMode || !editAreaData?.geom || !mapLoaded || showIndoorMap || !useMapboxView) return;
        if (!isIndoorLikeSpaceType(spaceType)) return;
        if (!canEditAreaGeometry) return;

        const draw = drawRef.current;
        const map = mapRef.current?.getMap();
        if (!draw || !map) return;

        const convertedGeom = activeSlamCanvas?.geoAnchor
            ? editAreaData.geom
            : activeSlamMapboxAnchor
                ? (() => {
                    const savedLocal = editAreaData?.configurations?.localGeom || editAreaData.geom;
                    if (!savedLocal) return null;
                    if (savedLocal.type === 'Point') {
                        const [mx, my] = savedLocal.coordinates;
                        const [lng, lat] = localMetersToGps(mx, my, activeSlamMapboxAnchor.lat, activeSlamMapboxAnchor.lng, activeSlamMapboxAnchor.bearingDegrees);
                        return { type: 'Point', coordinates: [lng, lat] };
                    }
                    if (savedLocal.type === 'Polygon' && Array.isArray(savedLocal.coordinates?.[0])) {
                        return {
                            type: 'Polygon',
                            coordinates: [savedLocal.coordinates[0].map(([mx, my]: [number, number]) =>
                                localMetersToGps(mx, my, activeSlamMapboxAnchor.lat, activeSlamMapboxAnchor.lng, activeSlamMapboxAnchor.bearingDegrees))],
                        };
                    }
                    return null;
                })()
                : null;

        if (!convertedGeom) return;
        try {
            if (convertedGeom.type === 'Point') {
                const [lng, lat] = convertedGeom.coordinates;
                placePointMarker(lng, lat);
                mapRef.current?.flyTo({ center: [lng, lat], zoom: 17 });
                return;
            }
            draw.deleteAll();
            draw.add({ type: 'Feature', properties: {}, geometry: convertedGeom } as any);
            refreshFromDraw();
            const coords = convertedGeom.coordinates?.[0];
            if (Array.isArray(coords) && coords.length > 0) {
                const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                mapRef.current?.flyTo({ center: [lon, lat], zoom: 17 });
            }
        } catch { /* draw context may be stale */ }
    }, [isEditMode, editAreaData, mapLoaded, showIndoorMap, useMapboxView, spaceType, canEditAreaGeometry, activeSlamCanvas, activeSlamMapboxAnchor, refreshFromDraw, placePointMarker]);

    // ── Reset mapLoaded when the Mapbox map unmounts (switching to indoor view) ─
    // Without this, mapLoaded stays true after the first Mapbox mount.  When the
    // user switches back to Mapbox the second time, effects that depend on
    // mapLoaded fire immediately with stale drawRef (from the destroyed previous
    // <Map>), and onMapLoad's `setMapLoaded(true)` is a no-op so the injection
    // effect never re-fires.  Resetting here ensures the injection + template
    // effects wait for the fresh map to load before running.
    useEffect(() => {
        if (showIndoorMap) {
            setMapLoaded(false);
            drawRef.current = null;
        }
    }, [showIndoorMap]);

    // ── Auto-lock spaceType when parent is indoor ─────────────────────────────
    useEffect(() => {
        if (parentIsIndoor && !isEditMode) {
            setSpaceType('indoor');
            // Default to polygon mode for indoor sub-areas (supports both template and manual drawing)
            setGeomMode('polygon');
            setSelectedSlamMapId(null);
            setSelectedSlamMapFull(null);
            setSlamPlacement(null);
            setSlamRotation(0);
        }
    }, [parentIsIndoor, isEditMode]);

    // ── Auto-switch to polygon mode when a template area type is selected (indoor) ──
    useEffect(() => {
        if (showIndoorMap && selectedTypeHasTemplate && selectedType?.templateDimensions) {
            setGeomMode('polygon');
        }
        // Non-template types keep whatever mode the user has selected (polygon or point)
    }, [showIndoorMap, selectedTypeHasTemplate, selectedType?.templateDimensions]);

    // ── Save ──────────────────────────────────────────────────────────────────

    const reassignUsedMarkers = async (targetAreaId: number | null) => {
        if (!targetAreaId || usedMarkerIds.length === 0) return;
        await Promise.all(usedMarkerIds.map((markerId) =>
            axios.patch(ENDPOINTS.MARKER_DETAIL(markerId), { areaId: targetAreaId }, { withCredentials: true })
        ));
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        const companyId = currentUser?.currentCompanyId || currentUser?.companyId;
        const markerAssignmentAreaId = values.parentAreaId ?? rootAreaId ?? null;

        let geom: any = null;

        if (isStagedMixedFlow && editStage !== 'areaGeometry') {
            message.error(editStage === 'slamPlacement'
                ? 'Finish placing the SLAM map before saving.'
                : 'Finish placing the SLAM position point before saving.');
            return;
        }

        if (showIndoorMap && !useMapboxView) {
            // Geometry comes from IndoorAreaMap canvas drawing (dual output: gpsGeom + localGeom)
            if (!indoorGeomResult || !indoorGeomResult.localGeom) {
                message.error(geomMode === 'point'
                    ? 'Click on the SLAM map to place a pin before saving.'
                    : 'Draw the area boundary on the SLAM map before saving (at least 3 points).');
                return;
            }
            // GPS-capable SLAM canvases (mixed parent, mixed owned area, or geo-anchored map)
            // store GPS as the primary geometry and preserve local metres in configurations.
            if (activeSlamCanEmitGps && indoorGeomResult.gpsGeom) {
                geom = indoorGeomResult.gpsGeom;
            } else {
                geom = indoorGeomResult.localGeom;
            }
        } else if (geomMode === 'point') {
            if (!pointCoords) {
                message.error('Click on the map to place a pin location before saving.');
                return;
            }
            geom = { type: 'Point', coordinates: [pointCoords.lng, pointCoords.lat] };
            if (spaceType === 'indoor' && useMapboxView && activeSlamMapboxAnchor) {
                geom = gpsGeometryToLocalMeters(geom, activeSlamMapboxAnchor);
            }
        } else {
            const draw = drawRef.current;
            if (!draw) { message.error('Map not ready.'); return; }
            const all = draw.getAll();
            const poly = all.features.find((f: any) => f.geometry?.type === 'Polygon');
            if (!poly) {
                message.error('Draw the area polygon before saving.');
                return;
            }
            const coords = poly.geometry.coordinates?.[0] || [];
            const trimmed =
                coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]
                    ? coords.slice(0, -1)
                    : coords;
            updateMarkers(coords);
            if (maxVertices && trimmed.length > maxVertices) {
                message.error(`This area type allows up to ${maxVertices} vertices.`);
                return;
            }
            geom = poly.geometry;
            if (spaceType === 'indoor' && useMapboxView && activeSlamMapboxAnchor) {
                const localGeom = gpsGeometryToLocalMeters(poly.geometry, activeSlamMapboxAnchor);
                if (!localGeom) {
                    message.error('Unable to convert Mapbox geometry back to indoor SLAM coordinates.');
                    return;
                }
                geom = localGeom;
            }
        }

        if (!companyId && !isEditMode) {
            message.error('No company context found. Select a company and try again.');
            return;
        }

        setSaving(true);
        try {
            let savedAreaId = areaIdFromUrl ? Number(areaIdFromUrl) : null;
            if (isEditMode && areaIdFromUrl) {
                const updatePayload: any = {
                    name: values.name,
                    code: values.code || null,
                    areaTypeId: values.areaTypeId,
                    parentAreaId: values.parentAreaId ?? null,
                    geom,
                    spaceType,
                    slamMapId: selectedSlamMapId || null,
                };
                const nextConfigurations: Record<string, any> = {
                    ...(editAreaData?.configurations || {}),
                };
                // Save SLAM placement for unanchored indoor maps
                if (slamPlacement && selectedSlamMapFull && !selectedSlamMapFull.geoAnchor) {
                    nextConfigurations.slamPlacement = { lat: slamPlacement.lat, lng: slamPlacement.lng, bearingDegrees: slamPlacement.bearingDegrees };
                }
                // Save local coordinates alongside GPS for GPS-capable SLAM drawings
                if (activeSlamCanEmitGps && indoorGeomResult?.localGeom && indoorGeomResult?.gpsGeom) {
                    nextConfigurations.localGeom = indoorGeomResult.localGeom;
                }
                updatePayload.configurations = Object.keys(nextConfigurations).length > 0 ? nextConfigurations : null;
                await axios.put(`${ENDPOINTS.AREAS_V2}/${areaIdFromUrl}`, updatePayload, { withCredentials: true });
                await reassignUsedMarkers(markerAssignmentAreaId);
                message.success('Area updated successfully');
            } else {
                const createPayload: any = {
                    name: values.name,
                    code: values.code || null,
                    areaTypeId: values.areaTypeId,
                    parentAreaId: values.parentAreaId ?? null,
                    companyId,
                    geom,
                    spaceType,
                    slamMapId: selectedSlamMapId || null,
                };
                // Save SLAM placement for unanchored indoor maps
                if (slamPlacement && selectedSlamMapFull && !selectedSlamMapFull.geoAnchor) {
                    createPayload.configurations = {
                        ...(createPayload.configurations || {}),
                        slamPlacement: { lat: slamPlacement.lat, lng: slamPlacement.lng, bearingDegrees: slamPlacement.bearingDegrees },
                    };
                }
                // Save local coordinates alongside GPS for GPS-capable SLAM drawings
                if (activeSlamCanEmitGps && indoorGeomResult?.localGeom && indoorGeomResult?.gpsGeom) {
                    createPayload.configurations = {
                        ...(createPayload.configurations || {}),
                        localGeom: indoorGeomResult.localGeom,
                    };
                }
                const response = await axios.post(ENDPOINTS.CREATE_AREA_V2, createPayload, { withCredentials: true });
                const createdAreaId = Number(response.data?.data?.id ?? response.data?.id);
                savedAreaId = Number.isInteger(createdAreaId) && createdAreaId > 0 ? createdAreaId : null;
                await reassignUsedMarkers(markerAssignmentAreaId);
                message.success('Area created successfully');
            }
            navigate(
                missionReturnPath && savedAreaId
                    ? appendCreatedAreaId(missionReturnPath, savedAreaId)
                    : missionReturnPath || '/areas'
            );
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || error.message || `Unable to ${isEditMode ? 'update' : 'create'} area`;
            message.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    // ── Point labels ──────────────────────────────────────────────────────────

    const pointLabels = useMemo(() => {
        if (!drawRef.current) return [];
        try {
            const all = drawRef.current.getAll();
            const poly = all.features.find((f: any) => f.geometry?.type === 'Polygon');
            if (!poly) return [];
            const coords = poly.geometry.coordinates?.[0] || [];
            const trimmed =
                coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]
                    ? coords.slice(0, -1)
                    : coords;
            return trimmed
                .filter((c: any) => c && c[0] !== undefined && c[1] !== undefined)
                .map((c: any, idx: number) => ({ idx: idx + 1, lon: c[0], lat: c[1] }));
        } catch { return []; /* draw's internal map context may be destroyed */ }
    }, [geomVersion]);

    // ── Sync editable coords from draw (only when user is not typing) ─────────

    useEffect(() => {
        if (!isEditingCoordsRef.current) {
            setCoordsOverride(pointLabels.map((p: any) => ({ lon: p.lon, lat: p.lat })));
        }
    }, [pointLabels]);

    // ── Auto-code ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!nameValue && !selectedType) return;
        const typeName = selectedType?.name || '';
        const slug = `${nameValue || ''}-${typeName}`.trim();
        if (!slug) return;
        const normalized = slug.toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '').replace(/-+/g, '-');
        form.setFieldsValue({ code: normalized });
    }, [nameValue, selectedType, form]);

    // ── Template image loading ────────────────────────────────────────────────

    useEffect(() => {
        if (!selectedTypeId) {
            setTemplateImageUrl(null);
            setSelectedTypeHasTemplate(false);
            return;
        }
        const selectedAreaType = areaTypes.find(t => t.id === selectedTypeId);
        if (!selectedAreaType) {
            setTemplateImageUrl(null);
            setSelectedTypeHasTemplate(false);
            return;
        }
        (async () => {
            try {
                const typeRes = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${selectedTypeId}`, { withCredentials: true });
                const hasTemplate = typeRes.data?.data?.templateImage || typeRes.data?.templateImage;
                setSelectedTypeHasTemplate(!!hasTemplate);
                if (!hasTemplate) { setTemplateImageUrl(null); return; }
                setTemplateImageUrl(`${ENDPOINTS.AREA_TYPES_V2}/${selectedTypeId}/template-image`);
            } catch {
                setTemplateImageUrl(null);
                setSelectedTypeHasTemplate(false);
            }
        })();
    }, [selectedTypeId]);

    useEffect(() => {
        if (!templateImageUrl || !drawRef.current) return;
        try {
            const all = drawRef.current.getAll();
            const poly = all.features.find((f: any) => f.geometry?.type === 'Polygon');
            if (poly) updateTemplateOverlay(poly.geometry.coordinates?.[0] || []);
        } catch { /* draw context may be stale */ }
    }, [templateImageUrl, updateTemplateOverlay]);

    useEffect(() => {
        if (isEditMode) return;
        if (!mapLoaded) return;
        if (!canEditAreaGeometry) return;
        // Don't auto-place a new template when switching to Mapbox if we already
        // have an indoor-drawn GPS polygon ready to inject (it takes priority).
        if (useMapboxView && indoorGeomResult?.gpsGeom) return;
        clearTemplateShape();
        if (!selectedTypeId || !selectedTypeHasTemplate) return;
        addTemplateShape();
    }, [selectedTypeId, mapLoaded, isEditMode, addTemplateShape, clearTemplateShape, selectedTypeHasTemplate, useMapboxView, indoorGeomResult, canEditAreaGeometry]);

    useEffect(() => {
        if (!mapLoaded || geomMode === 'point') return;
        const map = mapRef.current?.getMap();
        if (!map) return;
        const container = map.getContainer();
        const controls = container.querySelectorAll('.mapbox-gl-draw_ctrl-draw-btn, .mapbox-gl-draw_ctrl-icon-trash, .mapbox-gl-draw_trash');
        const showControls = canEditAreaGeometry && !selectedTypeHasTemplate;
        controls.forEach((btn) => { (btn as HTMLElement).style.display = showControls ? '' : 'none'; });
    }, [mapLoaded, selectedTypeHasTemplate, geomMode, canEditAreaGeometry]);

    // ── Parent area display on map ────────────────────────────────────────────

    useEffect(() => {
        if (!mapLoaded) return;
        const map = mapRef.current?.getMap();
        if (!map) return;
        try { if (map.getLayer('parent-area-outline')) map.removeLayer('parent-area-outline'); } catch { /* ignore */ }
        try { if (map.getLayer('parent-area-layer')) map.removeLayer('parent-area-layer'); } catch { /* ignore */ }
        try { if (map.getSource('parent-area')) map.removeSource('parent-area'); } catch { /* ignore */ }
        if (!selectedParentAreaId) return;
        (async () => {
            try {
                const res = await axios.get(`${ENDPOINTS.AREAS_V2}/${selectedParentAreaId}`, { withCredentials: true });
                const parentArea = res.data?.data || res.data;
                if (parentArea?.geom && parentArea.geom.type !== 'Point') {
                    if (!map.getSource('parent-area')) {
                        map.addSource('parent-area', { type: 'geojson', data: parentArea.geom });
                    }
                    if (!map.getLayer('parent-area-layer')) {
                        map.addLayer({ id: 'parent-area-layer', type: 'fill', source: 'parent-area', paint: { 'fill-color': '#3388ff', 'fill-opacity': 0.2 } });
                    }
                    if (!map.getLayer('parent-area-outline')) {
                        map.addLayer({ id: 'parent-area-outline', type: 'line', source: 'parent-area', paint: { 'line-color': '#3388ff', 'line-width': 2 } });
                    }
                    const coords = parentArea.geom.coordinates[0];
                    if (coords && coords.length > 0) {
                        const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                        const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                        map.flyTo({ center: [lon, lat], zoom: 16 });
                    }
                }
            } catch { /* ignore */ }
        })();
    }, [selectedParentAreaId, mapLoaded]);

    // ── Fetch selected SLAM map full data for GPS overlay ─────────────────────
    // Uses the backend /image-stream proxy to avoid GCS CORS issues with Mapbox.
    // We fetch via axios (which includes auth cookies) then create a blob URL
    // so Mapbox's internal image source can load it without auth headers.

    const slamBlobUrlRef = useRef<string | null>(null);

    useEffect(() => {
        // Revoke previous blob URL
        if (slamBlobUrlRef.current) {
            URL.revokeObjectURL(slamBlobUrlRef.current);
            slamBlobUrlRef.current = null;
        }

        if (!selectedSlamMapId) {
            setSelectedSlamMapFull(null);
            setSelectedSlamCanvasMap(null);
            setSlamPlacement(null);
            setSlamRotation(0);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                // 1. Fetch SLAM map detail
                const detailRes = await axios.get(ENDPOINTS.SLAM_MAP_DETAIL(selectedSlamMapId), { withCredentials: true });
                if (cancelled) return;
                const detail = detailRes.data?.data || detailRes.data;

                if (!detail) {
                    message.error('Unable to load SLAM map details.');
                    setSelectedSlamMapFull(null);
                    setSelectedSlamCanvasMap(null);
                    return;
                }
                // 2. Fetch image via backend proxy (with auth cookies) and create blob URL for Mapbox
                const proxyImageUrl = ENDPOINTS.SLAM_MAP_IMAGE_STREAM(selectedSlamMapId);
                let blobUrl: string;
                try {
                    const imgResponse = await axios.get(proxyImageUrl, {
                        withCredentials: true,
                        responseType: 'blob',
                    });
                    if (cancelled) return;
                    blobUrl = URL.createObjectURL(imgResponse.data);
                    slamBlobUrlRef.current = blobUrl;
                } catch (fetchErr: any) {
                    console.error('[AreaCreate] Failed to fetch SLAM image via proxy:', fetchErr);
                    if (!cancelled) {
                        message.error('Unable to load SLAM map image. Please check the image has been uploaded.');
                        setSelectedSlamMapFull(null);
                        setSelectedSlamCanvasMap(null);
                    }
                    return;
                }
                if (cancelled) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }

                // 3. Validate required metadata
                const imgW = detail.imageWidth || detail.widthPixels;
                const imgH = detail.imageHeight || detail.heightPixels;
                const mpp = detail.metersPerPixel || detail.resolution;
                const missingFields: string[] = [];
                if (!imgW || !Number.isFinite(Number(imgW))) missingFields.push('imageWidth');
                if (!imgH || !Number.isFinite(Number(imgH))) missingFields.push('imageHeight');
                if (!mpp || !Number.isFinite(Number(mpp))) missingFields.push('metersPerPixel');

                if (missingFields.length > 0) {
                    message.error(
                        `SLAM map "${detail.name || 'unknown'}" is missing required metadata: ${missingFields.join(', ')}. ` +
                        `Please re-upload the map image with a companion map.yaml file.`
                    );
                    setSelectedSlamMapFull(null);
                    setSelectedSlamCanvasMap(null);
                    return;
                }

                // 4. Build overlay data with same-origin blob URL (Mapbox can load it)
                const overlayData: SlamOverlayData = {
                    imageUrl: blobUrl,
                    imageWidth: Number(imgW),
                    imageHeight: Number(imgH),
                    metersPerPixel: Number(mpp),
                    geoAnchor: detail.geoAnchor || null,
                };
                setSelectedSlamMapFull(overlayData);
                setSelectedSlamCanvasMap({
                    id: Number(detail.id || selectedSlamMapId),
                    name: detail.name || 'SLAM map',
                    imageUrl: blobUrl,
                    imageWidth: Number(imgW),
                    imageHeight: Number(imgH),
                    metersPerPixel: Number(mpp),
                    originX: detail.originX ?? null,
                    originY: detail.originY ?? null,
                    rotationDegrees: detail.rotationDegrees ?? null,
                    geoAnchor: detail.geoAnchor || null,
                });
                console.log('[AreaCreate] SLAM overlay data ready:', {
                    imageUrl: proxyImageUrl,
                    imageWidth: overlayData.imageWidth,
                    imageHeight: overlayData.imageHeight,
                    metersPerPixel: overlayData.metersPerPixel,
                    geoAnchor: !!overlayData.geoAnchor,
                });

                // If anchored, auto-compute center from anchor; if not, default to current map center
                if (overlayData.geoAnchor) {
                    setSlamPlacement(null);
                    setSlamRotation(overlayData.geoAnchor.bearingDegrees || 0);
                } else {
                    const saved = editAreaData?.configurations?.slamPlacement;
                    const mapCenter = mapRef.current?.getMap()?.getCenter();
                    setSlamPlacement({
                        lat: saved?.lat != null ? Number(saved.lat) : (mapCenter?.lat ?? viewState.latitude),
                        lng: saved?.lng != null ? Number(saved.lng) : (mapCenter?.lng ?? viewState.longitude),
                        bearingDegrees: Number(saved?.bearingDegrees ?? 0),
                    });
                    setSlamRotation(Number(saved?.bearingDegrees ?? 0));
                }
            } catch (err: any) {
                console.error('[AreaCreate] fetchSlamMapDetail error:', err?.response?.data || err?.message);
                if (!cancelled) {
                    message.error('Failed to load SLAM map data. Check the console for details.');
                    setSelectedSlamMapFull(null);
                    setSelectedSlamCanvasMap(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedSlamMapId, editAreaData, viewState.latitude, viewState.longitude]);


    // ── Fetch parent SLAM map for IndoorAreaMap when parent is indoor/mixed ───

    useEffect(() => {
        if (!selectedParentAreaId) { setParentSlamMapFull(null); return; }
        const parentArea = areas.find(a => a.id === selectedParentAreaId);
        if (!parentArea || (parentArea.spaceType !== 'indoor' && parentArea.spaceType !== 'mixed')) {
            setParentSlamMapFull(null);
            return;
        }
        let cancelled = false;
        setParentSlamMapLoading(true);
        setParentSlamMapFull(null);
        axios.get(ENDPOINTS.AREA_SLAM_MAP_LATEST(selectedParentAreaId), { withCredentials: true })
            .then(res => {
                if (!cancelled) {
                    const m = res.data?.data;
                    if (m?.imageUrl) setParentSlamMapFull(m as SlamMapData);
                    else setParentSlamMapFull(null);
                }
            })
            .catch(() => { if (!cancelled) setParentSlamMapFull(null); })
            .finally(() => { if (!cancelled) setParentSlamMapLoading(false); });
        return () => { cancelled = true; };
    }, [selectedParentAreaId, areas]);

    // ── Fetch parent area's saved slamPlacement independently (for manualAnchor) ──
    // This runs regardless of useMapboxView so IndoorAreaMap always has the anchor
    // needed to convert local coordinates to GPS for Mapbox view.

    useEffect(() => {
        if (!selectedParentAreaId) { setParentAreaSlamPlacement(null); return; }
        const parentArea = areas.find(a => a.id === selectedParentAreaId);
        if (!parentArea || (parentArea.spaceType !== 'mixed' && parentArea.spaceType !== 'indoor')) {
            setParentAreaSlamPlacement(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${ENDPOINTS.AREAS_V2}/${selectedParentAreaId}`, { withCredentials: true });
                if (cancelled) return;
                const areaData = res.data?.data || res.data;
                const saved = areaData?.configurations?.slamPlacement;
                if (saved?.lat != null && saved?.lng != null) {
                    setParentAreaSlamPlacement({
                        lat: Number(saved.lat),
                        lng: Number(saved.lng),
                        bearingDegrees: Number(saved.bearingDegrees ?? 0),
                    });
                } else {
                    // Fallback: use the parent area's own geometry centroid as anchor
                    if (areaData?.geom?.type === 'Point') {
                        const [lng, lat] = areaData.geom.coordinates;
                        setParentAreaSlamPlacement({ lat, lng, bearingDegrees: 0 });
                    } else if (areaData?.geom?.type === 'Polygon' && areaData.geom.coordinates?.[0]?.length > 0) {
                        const coords = areaData.geom.coordinates[0];
                        const lng = coords.reduce((s: number, c: any) => s + c[0], 0) / coords.length;
                        const lat = coords.reduce((s: number, c: any) => s + c[1], 0) / coords.length;
                        setParentAreaSlamPlacement({ lat, lng, bearingDegrees: 0 });
                    } else {
                        setParentAreaSlamPlacement(null);
                    }
                }
            } catch {
                if (!cancelled) setParentAreaSlamPlacement(null);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedParentAreaId, areas]);

    // ── Build parent SLAM overlay for Mapbox view (non-editable) ──────────────

    useEffect(() => {
        // Revoke previous blob URL
        if (parentSlamBlobUrlRef.current) {
            URL.revokeObjectURL(parentSlamBlobUrlRef.current);
            parentSlamBlobUrlRef.current = null;
        }

        // Only build when in Mapbox view mode with a parent SLAM map
        if (!useMapboxView || !parentSlamMapFull || !(parentSlamMapFull as any).id) {
            setParentSlamOverlayData(null);
            setParentSlamPlacement(null);
            return;
        }

        const slamMapId = (parentSlamMapFull as any).id as number;
        let cancelled = false;

        (async () => {
            try {
                // 1. Fetch SLAM map detail for metadata + parent area geometry for placement
                const [detailRes, parentAreaRes] = await Promise.all([
                    axios.get(ENDPOINTS.SLAM_MAP_DETAIL(slamMapId), { withCredentials: true }),
                    selectedParentAreaId
                        ? axios.get(`${ENDPOINTS.AREAS_V2}/${selectedParentAreaId}`, { withCredentials: true })
                        : Promise.resolve(null),
                ]);
                if (cancelled) return;
                const detail = detailRes.data?.data || detailRes.data;
                if (!detail) { setParentSlamOverlayData(null); return; }

                // 2. Read parent area's saved slamPlacement from configurations
                let savedPlacement: SlamPlacement | null = null;
                if (parentAreaRes) {
                    const parentArea = parentAreaRes.data?.data || parentAreaRes.data;
                    const saved = parentArea?.configurations?.slamPlacement;
                    if (saved?.lat != null && saved?.lng != null) {
                        savedPlacement = {
                            lat: Number(saved.lat),
                            lng: Number(saved.lng),
                            bearingDegrees: Number(saved.bearingDegrees ?? 0),
                        };
                    }
                }

                // 3. Fetch image via proxy as blob
                const proxyUrl = ENDPOINTS.SLAM_MAP_IMAGE_STREAM(slamMapId);
                let blobUrl: string;
                try {
                    const imgRes = await axios.get(proxyUrl, { withCredentials: true, responseType: 'blob' });
                    if (cancelled) return;
                    blobUrl = URL.createObjectURL(imgRes.data);
                    parentSlamBlobUrlRef.current = blobUrl;
                } catch {
                    if (!cancelled) setParentSlamOverlayData(null);
                    return;
                }
                if (cancelled) { URL.revokeObjectURL(blobUrl); return; }

                // 4. Validate metadata
                const imgW = detail.imageWidth || detail.widthPixels;
                const imgH = detail.imageHeight || detail.heightPixels;
                const mpp = detail.metersPerPixel || detail.resolution;
                if (!imgW || !imgH || !mpp) { setParentSlamOverlayData(null); return; }

                // 5. Build overlay data
                const overlay: SlamOverlayData = {
                    imageUrl: blobUrl,
                    imageWidth: Number(imgW),
                    imageHeight: Number(imgH),
                    metersPerPixel: Number(mpp),
                    geoAnchor: detail.geoAnchor || null,
                };
                console.log('[AreaCreate] Parent SLAM overlay DEBUG — raw detail from API:', JSON.stringify({
                    id: detail.id,
                    name: detail.name,
                    imageWidth: detail.imageWidth,
                    imageHeight: detail.imageHeight,
                    widthPixels: detail.widthPixels,
                    heightPixels: detail.heightPixels,
                    metersPerPixel: detail.metersPerPixel,
                    resolution: detail.resolution,
                    rotationDegrees: detail.rotationDegrees,
                    geoAnchor: detail.geoAnchor,
                }, null, 2));
                console.log('[AreaCreate] Parent SLAM overlay — final overlay data:', {
                    imageWidth: overlay.imageWidth,
                    imageHeight: overlay.imageHeight,
                    metersPerPixel: overlay.metersPerPixel,
                    geoAnchor: overlay.geoAnchor,
                    savedPlacement,
                });
                setParentSlamOverlayData(overlay);

                // 6. Set placement: use parent area's saved slamPlacement; anchored maps need none
                if (!detail.geoAnchor) {
                    setParentSlamPlacement(savedPlacement);
                } else {
                    setParentSlamPlacement(null); // anchored maps don't need placement
                }
            } catch {
                if (!cancelled) setParentSlamOverlayData(null);
            }
        })();

        return () => { cancelled = true; };
    }, [useMapboxView, parentSlamMapFull, selectedParentAreaId]);

    // ── Inject indoor polygon into MapboxDraw when switching to Mapbox view ───
    // When the user draws a polygon on the indoor SLAM map and then switches to
    // the Mapbox satellite view, transfer the GPS-converted polygon to MapboxDraw
    // so they can see and adjust it on the satellite map.
    useEffect(() => {
        if (!useMapboxView || !mapLoaded) return;
        if (!indoorGeomResult?.gpsGeom) return;
        const gpsGeom = indoorGeomResult.gpsGeom;
        if (gpsGeom.type !== 'Polygon' || !gpsGeom.coordinates?.[0]?.length) return;

        const draw = drawRef.current;
        const map = mapRef.current?.getMap();
        if (!draw || !map) return;

        // Clear any existing polygon (e.g. auto-placed template) — the indoor-drawn
        // GPS polygon always takes priority when switching from Local → Mapbox.
        try {
            autoAddingTemplateRef.current = true;
            draw.deleteAll();
            draw.add({
                type: 'Feature',
                properties: {},
                geometry: gpsGeom,
            } as any);
            setTimeout(() => { autoAddingTemplateRef.current = false; }, 0);
            refreshFromDraw();
        } catch { /* draw context may be stale after view switch */ }

        // Fly the map camera to the polygon bounds
        try {
            const coords: [number, number][] = gpsGeom.coordinates[0];
            const bounds = coords.reduce(
                (b, c) => b.extend(c as [number, number]),
                new mapboxgl.LngLatBounds(coords[0], coords[0]),
            );
            map.fitBounds(bounds, { padding: 80, maxZoom: 19 });
        } catch { /* bounds calculation failed — not critical */ }
    }, [useMapboxView, mapLoaded, indoorGeomResult, refreshFromDraw]);

    // Cleanup parent SLAM blob URL on unmount
    useEffect(() => {
        return () => {
            if (parentSlamBlobUrlRef.current) URL.revokeObjectURL(parentSlamBlobUrlRef.current);
            if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
        };
    }, []);

    if (loading) return <Spin style={{ marginTop: 40 }} />;

    // ── Render ────────────────────────────────────────────────────────────────

    const isIndoorLike = spaceType === 'indoor' || spaceType === 'mixed';
    // showIndoorMap is already computed above (before handleSave)
    const savedLocalGeom = editAreaData?.configurations?.localGeom || null;
    const editAreaTypeId = editAreaData?.areaType?.id ?? null;
    const selectedTypeMatchesEditArea = !isEditMode || !editAreaTypeId || selectedTypeId === editAreaTypeId;
    const indoorExistingGeom = transferGeom || (isEditMode && selectedTypeMatchesEditArea
        ? activeSlamCanvas?.geoAnchor
            ? editAreaData?.geom
            : activeSlamCanEmitGps
                ? (savedLocalGeom || gpsGeometryToLocalMeters(editAreaData?.geom, activeSlamManualAnchor))
                : editAreaData?.geom
        : null);

    return (
        <div className="area-editor-page">
            <Row justify="space-between" align="middle" className="area-editor-header">
                <Col>
                    <h2>{isEditMode ? 'Edit area' : 'Create area'}</h2>
                    <div className="area-editor-subtitle">
                        {isEditMode
                            ? 'Modify the area details and geometry.'
                            : 'Define the area type, space type, location, and boundary.'}
                    </div>
                </Col>
                <Col>
                    <Space className="area-create-actions">
                        <Button className="area-create-cancel-button" onClick={() => navigate(missionReturnPath || '/areas')}>
                            Cancel
                        </Button>
                        <Button className="area-create-save-button" type="primary" onClick={handleSave} loading={saving}>
                            {isEditMode ? 'Update' : 'Save'}
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={8}>
                    <Card title="Details" className="area-create-details-card">
                        <Form layout="vertical" form={form}>

                            {/* ── Space type selector ── */}
                            <Form.Item label="Space type" className="area-create-type-field">
                                <Radio.Group
                                    value={spaceType}
                                    disabled={parentIsIndoor && !isEditMode}
                                    onChange={(e) => {
                                        const newSpaceType = e.target.value as SpaceType;
                                        setSpaceType(newSpaceType);
                                        setSelectedSlamMapId(null);
                                        setSelectedSlamMapFull(null);
                                        setSlamPlacement(null);
                                        setSlamRotation(0);
                                        if (newSpaceType === 'outdoor') setGeomMode('polygon');
                                        if (newSpaceType === 'indoor') setGeomMode('point');
                                        // Auto-clear area type if it's no longer valid for the new space type
                                        if (selectedTypeId) {
                                            const currentType = areaTypes.find(t => t.id === selectedTypeId);
                                            if (currentType) {
                                                let valid = false;
                                                if (parentIsIndoor && !isEditMode) {
                                                    valid = currentType.spaceType === 'indoor' || !currentType.spaceType;
                                                } else if (newSpaceType === 'outdoor') {
                                                    valid = !currentType.spaceType || currentType.spaceType === 'outdoor';
                                                } else if (newSpaceType === 'indoor') {
                                                    valid = currentType.spaceType === 'indoor';
                                                } else if (newSpaceType === 'mixed') {
                                                    valid = currentType.spaceType === 'mixed' || !currentType.spaceType;
                                                }
                                                if (!valid) {
                                                    setSelectedTypeId(null);
                                                    form.setFieldsValue({ areaTypeId: undefined });
                                                }
                                            }
                                        }
                                    }}
                                    buttonStyle="solid"
                                    size="small"
                                >
                                    <Radio.Button value="outdoor">
                                        <EnvironmentOutlined /> Outdoor
                                    </Radio.Button>
                                    <Radio.Button value="indoor">
                                        <HomeOutlined /> Indoor
                                    </Radio.Button>
                                    <Radio.Button value="mixed">
                                        <PartitionOutlined /> Mixed
                                    </Radio.Button>
                                </Radio.Group>
                                {parentIsIndoor && !isEditMode && (
                                    <div style={{ marginTop: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            🔒 Space type is locked to <strong>Indoor</strong> because the parent area is indoor.
                                        </Text>
                                    </div>
                                )}
                                {spaceType === 'indoor' && !(parentIsIndoor && !isEditMode) && (
                                    <div style={{ marginTop: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            Click on the map to place the area pin. Select a SLAM map to see it overlaid on the GPS map.
                                        </Text>
                                        {pointCoords && (
                                            <div style={{ marginTop: 4, fontSize: 11, color: '#52c41a' }}>
                                                📍 {pointCoords.lat.toFixed(6)}, {pointCoords.lng.toFixed(6)}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {spaceType === 'mixed' && (
                                    <div style={{ marginTop: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            Indoor space with GPS anchor. Sub-areas can use GPS coordinates.
                                        </Text>
                                    </div>
                                )}
                            </Form.Item>

                            <Form.Item
                                label="Area type"
                                name="areaTypeId"
                                className="area-create-type-field"
                                rules={[{ required: true, message: 'Select an area type' }]}
                            >
                                <Select
                                    placeholder="Select type"
                                    onChange={(val) => setSelectedTypeId(val)}
                                >
                                    {areaTypes
                                        .filter((t) => {
                                            // When parent is indoor: only indoor + any (null) types
                                            if (parentIsIndoor && !isEditMode) {
                                                return t.spaceType === 'indoor' || !t.spaceType;
                                            }
                                            // outdoor: outdoor + any (null)
                                            if (spaceType === 'outdoor') {
                                                return !t.spaceType || t.spaceType === 'outdoor';
                                            }
                                            // indoor: indoor + any (null)
                                            if (spaceType === 'indoor') {
                                                return t.spaceType === 'indoor' || !t.spaceType;
                                            }
                                            // mixed: mixed + any (null)
                                            if (spaceType === 'mixed') {
                                                return t.spaceType === 'mixed' || !t.spaceType;
                                            }
                                            return true;
                                        })
                                        .map((t) => (
                                        <Select.Option key={t.id} value={t.id}>
                                            {t.spaceType === 'indoor' ? '🏠 ' : t.spaceType === 'mixed' ? '⚡ ' : ''}
                                            {t.name} {t.maxVertices ? `(${t.maxVertices} vertices max)` : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Parent area"
                                name="parentAreaId"
                                className="area-create-parent-field"
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Select parent area"
                                    allowClear
                                    onChange={(val) => setSelectedParentAreaId(val != null ? val as number : null)}
                                >
                                    {areas.map((a) => (
                                        <Select.Option key={a.id} value={a.id}>
                                            {a.spaceType === 'indoor' ? '🏠 ' : a.spaceType === 'mixed' ? '⚡ ' : ''}{a.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Name"
                                name="name"
                                className="area-create-name-field"
                                rules={[{ required: true, message: 'Enter a name' }]}
                            >
                                <Input onChange={(e) => setNameValue(e.target.value)} />
                            </Form.Item>

                            <Form.Item label="Code" name="code" className="area-create-code-field">
                                <Input disabled style={{ color: '#595959', backgroundColor: '#f5f5f5' }} />
                            </Form.Item>

                            {maxVertices && (
                                <div style={{ marginTop: -8, marginBottom: 8, color: '#fa8c16' }}>
                                    <Text>Max {maxVertices} vertices allowed for this type.</Text>
                                </div>
                            )}

                            {/* ── Geometry mode (indoor sub-areas + mixed) ── */}
                            {(spaceType === 'mixed' || showIndoorMap) && (
                                <Form.Item label="Geometry" className="area-create-type-field">
                                    <Radio.Group
                                        value={geomMode}
                                        disabled={!canEditAreaGeometry || (showIndoorMap && selectedTypeHasTemplate && !!templateImageUrl)}
                                        onChange={(e) => {
                                            setGeomMode(e.target.value);
                                            if (e.target.value === 'polygon') clearPointMarker();
                                            if (e.target.value === 'point') {
                                                const draw = drawRef.current;
                                                if (draw) { draw.deleteAll(); refreshFromDraw(); }
                                            }
                                        }}
                                        buttonStyle="outline"
                                        size="small"
                                    >
                                        <Radio.Button value="polygon">Polygon boundary</Radio.Button>
                                        <Radio.Button value="point">Point / pin</Radio.Button>
                                    </Radio.Group>
                                    {showIndoorMap && selectedTypeHasTemplate && !!templateImageUrl && (
                                        <div style={{ marginTop: 6 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                🔒 Geometry is locked to polygon because a template shape is applied. Drag corners on the map to reposition.
                                            </Text>
                                        </div>
                                    )}
                                    {!canEditAreaGeometry && (
                                        <div style={{ marginTop: 6 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Finish SLAM placement and the position point before editing the area geometry.
                                            </Text>
                                        </div>
                                    )}
                                    {geomMode === 'polygon' && showIndoorMap && !(selectedTypeHasTemplate && !!templateImageUrl) && (
                                        <div style={{ marginTop: 6 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Click on the SLAM map to add polygon points. Drag corners to adjust.
                                            </Text>
                                        </div>
                                    )}
                                    {geomMode === 'point' && (
                                        <div style={{ marginTop: 6 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Click on the map to place the area location pin.
                                            </Text>
                                        </div>
                                    )}
                                    {pointCoords && geomMode === 'point' && !showIndoorMap && (
                                        <div style={{ marginTop: 4, fontSize: 11, color: '#52c41a' }}>
                                            📍 {pointCoords.lat.toFixed(6)}, {pointCoords.lng.toFixed(6)}
                                        </div>
                                    )}
                                </Form.Item>
                            )}
                        </Form>

                        {/* ── SLAM Map Picker ── */}
                        {isIndoorLike && (
                            <>
                                <Divider plain style={{ margin: '12px 0' }}>SLAM Map</Divider>
                                {slamMapsLoading ? (
                                    <div style={{ textAlign: 'center', padding: 16 }}>
                                        <Spin size="small" />
                                        <Text type="secondary" style={{ marginLeft: 8 }}>Loading maps…</Text>
                                    </div>
                                ) : availableSlamMaps.length === 0 ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                No SLAM maps found. Upload maps in the SLAM Maps section first.
                                            </Text>
                                        }
                                    />
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        {availableSlamMaps
                                        .filter((map) => !parentSlamMapFull?.id || map.id !== parentSlamMapFull.id)
                                        .map((map) => {
                                            const isSelected = selectedSlamMapId === map.id;
                                            return (
                                                <div
                                                    key={map.id}
                                                    onClick={() => setSelectedSlamMapId(isSelected ? null : map.id)}
                                                    style={{
                                                        border: `2px solid ${isSelected ? '#1677ff' : '#d9d9d9'}`,
                                                        borderRadius: 6,
                                                        padding: 6,
                                                        cursor: 'pointer',
                                                        background: isSelected ? '#e6f4ff' : '#fff',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    {map.imageUrl ? (
                                                        <Image
                                                            src={map.imageUrl}
                                                            alt={map.name}
                                                            style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 4 }}
                                                            preview={false}
                                                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '100%', height: 70, background: '#f5f5f5',
                                                            borderRadius: 4, display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', color: '#bfbfbf', fontSize: 11
                                                        }}>
                                                            No image
                                                        </div>
                                                    )}
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text
                                                            style={{ fontSize: 11, fontWeight: isSelected ? 600 : 400 }}
                                                            ellipsis
                                                        >
                                                            {map.name}
                                                        </Text>
                                                        <br />
                                                        <Tag style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                                                            {map.mapType}
                                                        </Tag>
                                                        {!map.areaId && (
                                                            <Tag color="blue" style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                                                                shared
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {selectedSlamMapId && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0, marginTop: 4 }}
                                        onClick={() => setSelectedSlamMapId(null)}
                                    >
                                        Clear SLAM map selection
                                    </Button>
                                )}
                            </>
                        )}

                        {/* ── Template preview ── */}
                        {templateImageUrl && geomMode === 'polygon' && (
                            <>
                                <Divider plain style={{ margin: '12px 0' }}>Template Preview</Divider>
                                <div style={{ padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, backgroundColor: '#fafafa', textAlign: 'center' }}>
                                    <img
                                        src={templateImageUrl}
                                        alt="Area template"
                                        style={{ maxWidth: '100%', height: 'auto', maxHeight: 150, objectFit: 'contain' }}
                                    />
                                </div>
                                <Button block style={{ marginTop: 8 }} onClick={clearTemplateShape}>
                                    Remove template shape
                                </Button>
                            </>
                        )}

                    </Card>
                </Col>

                <Col xs={24} xl={16}>
                    {/* ── Map view toggle: shown when the active SLAM canvas can be represented on GPS map ── */}
                    {activeSlamCanShowMapbox && activeSlamCanvas && (
                        <div style={{ marginBottom: 12 }}>
                            <Segmented
                                value={useMapboxView ? 'mapbox' : 'local'}
                                onChange={(val) => {
                                    const toMapbox = val === 'mapbox';
                                    setUseMapboxView(toMapbox);
                                    if (!toMapbox) {
                                        // Switching Mapbox → Local: capture the current GPS polygon from
                                        // MapboxDraw (user may have edited it) and convert to local metres
                                        // so IndoorAreaMap can re-render it.
                                        const draw = drawRef.current;
                                        if (draw && activeSlamMapboxAnchor) {
                                            try {
                                                const all = draw.getAll();
                                                const poly = all.features.find((f: any) => f.geometry?.type === 'Polygon');
                                                if (poly?.geometry?.coordinates?.[0]?.length) {
                                                    const gpsRing: [number, number][] = poly.geometry.coordinates[0];
                                                    const anchor = activeSlamMapboxAnchor;
                                                    const localRing: [number, number][] = gpsRing.map(([lng, lat]) =>
                                                        gpsToLocalMeters(lat, lng, anchor.lat, anchor.lng, anchor.bearingDegrees));
                                                    setTransferGeom({
                                                        type: 'Polygon',
                                                        coordinates: [localRing],
                                                    });
                                                }
                                            } catch { /* draw context stale — safe to ignore */ }
                                        } else if (indoorGeomResult?.localGeom) {
                                            // No draw polygon — fall back to the last indoor-drawn local geom
                                            setTransferGeom(indoorGeomResult.localGeom);
                                        }
                                        setIndoorGeomResult(null);
                                    } else {
                                        // Switching Local → Mapbox: clear transferGeom so it doesn't interfere
                                        setTransferGeom(null);
                                    }
                                    clearPointMarker();
                                    // Only clear draw when switching TO local — the indoor map
                                    // manages its own state. When switching TO mapbox the
                                    // injection useEffect will populate draw with the GPS polygon.
                                    if (!toMapbox) {
                                        try {
                                            const draw = drawRef.current;
                                            if (draw) { draw.deleteAll(); refreshFromDraw(); }
                                        } catch { /* draw context stale — safe to ignore */ }
                                    }
                                    // When switching to Mapbox, fly to the parent/current area's location
                                    if (toMapbox) {
                                        (async () => {
                                            try {
                                                const targetAreaId = selectedParentAreaId || areaIdFromUrl;
                                                if (!targetAreaId) return;
                                                const res = await axios.get(`${ENDPOINTS.AREAS_V2}/${targetAreaId}`, { withCredentials: true });
                                                const targetArea = res.data?.data || res.data;
                                                if (targetArea?.geom) {
                                                    let lng: number | undefined, lat: number | undefined;
                                                    if (targetArea.geom.type === 'Point') {
                                                        [lng, lat] = targetArea.geom.coordinates;
                                                    } else if (targetArea.geom.coordinates?.[0]?.length > 0) {
                                                        const coords = targetArea.geom.coordinates[0];
                                                        lng = coords.reduce((s: number, c: any) => s + c[0], 0) / coords.length;
                                                        lat = coords.reduce((s: number, c: any) => s + c[1], 0) / coords.length;
                                                    }
                                                    if (lng !== undefined && lat !== undefined) {
                                                        setViewState(prev => ({ ...prev, longitude: lng!, latitude: lat!, zoom: 16 }));
                                                        // If the Mapbox map is already mounted, fly to it
                                                        setTimeout(() => {
                                                            mapRef.current?.flyTo({ center: [lng!, lat!], zoom: 16 });
                                                        }, 300);
                                                    }
                                                } else if (activeSlamMapboxAnchor) {
                                                    const lng = activeSlamMapboxAnchor.lng;
                                                    const lat = activeSlamMapboxAnchor.lat;
                                                    setViewState(prev => ({ ...prev, longitude: lng, latitude: lat, zoom: 16 }));
                                                    setTimeout(() => {
                                                        mapRef.current?.flyTo({ center: [lng, lat], zoom: 16 });
                                                    }, 300);
                                                }
                                            } catch { /* non-critical */ }
                                        })();
                                    }
                                }}
                                options={[
                                    { label: '🏠 Local Coordinates', value: 'local' },
                                    { label: '🗺️ Mapbox Map', value: 'mapbox' },
                                ]}
                                block
                                style={{ fontWeight: 500 }}
                            />
                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                                {useMapboxView
                                    ? 'Using GPS / Mapbox satellite map — draw area on the map like outdoor areas.'
                                    : 'Using the indoor SLAM map — draw area in local coordinates.'}
                            </Text>
                        </div>
                    )}

                    {isStagedMixedFlow && (
                        <Card size="small" style={{ marginBottom: 12 }}>
                            <Space wrap align="center">
                                <Text strong>
                                    {editStage === 'slamPlacement'
                                        ? 'SLAM placement'
                                        : editStage === 'anchorPoint'
                                            ? 'Position point'
                                            : 'Area geometry'}
                                </Text>
                                {editStage === 'slamPlacement' && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => {
                                            setAnchorPointArmed(false);
                                            setEditStage(editAreaSavedPlacement || pointCoords ? 'areaGeometry' : 'anchorPoint');
                                        }}
                                    >
                                        Done placing SLAM
                                    </Button>
                                )}
                                {editStage === 'anchorPoint' && (
                                    <>
                                        <Button
                                            size="small"
                                            type={anchorPointArmed ? 'primary' : 'default'}
                                            onClick={() => setAnchorPointArmed(true)}
                                        >
                                            {pointCoords ? 'Replace point' : 'Add point'}
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="small"
                                            disabled={!pointCoords}
                                            onClick={() => {
                                                setAnchorPointArmed(false);
                                                setEditStage('areaGeometry');
                                            }}
                                        >
                                            Done placing point
                                        </Button>
                                    </>
                                )}
                                {editStage === 'areaGeometry' && (
                                    <>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setUseMapboxView(true);
                                                setAnchorPointArmed(false);
                                                setEditStage('slamPlacement');
                                            }}
                                        >
                                            Edit SLAM placement
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setUseMapboxView(true);
                                                setAnchorPointArmed(false);
                                                setEditStage('anchorPoint');
                                            }}
                                        >
                                            Edit point
                                        </Button>
                                    </>
                                )}
                                {editStage === 'anchorPoint' && pointCoords && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {pointCoords.lat.toFixed(6)}, {pointCoords.lng.toFixed(6)}
                                    </Text>
                                )}
                            </Space>
                        </Card>
                    )}

                    {/* ── IndoorAreaMap: shown when an active parent/owned SLAM canvas is available ── */}
                    {showIndoorMap ? (
                        <Card
                            title={
                                <Space>
                                    <HomeOutlined style={{ color: '#722ed1' }} />
                                    <span>Indoor map — {activeSlamCanvas?.name}</span>
                                </Space>
                            }
                            className="area-create-map-card"
                        >
                            <div style={{ height: 520, padding: 4 }}>
                                <IndoorAreaMap
                                    slamMap={activeSlamCanvas!}
                                    mode={geomMode}
                                    templateImageUrl={templateImageUrl}
                                    templateDimensions={selectedType?.templateDimensions ?? null}
                                    existingGeom={indoorExistingGeom}
                                    manualAnchor={activeSlamMapboxAnchor || undefined}
                                    maxVertices={maxVertices}
                                    canDraw={!!selectedTypeId && canEditAreaGeometry}
                                    markers={indoorMapMarkers}
                                    onMarkerSelect={handleIndoorMarkerSelect}
                                    onClear={() => {
                                        // Reset area type selection so user must pick again
                                        setSelectedTypeId(null);
                                        setSelectedTypeHasTemplate(false);
                                        setTemplateImageUrl(null);
                                        form.setFieldsValue({ areaTypeId: undefined });
                                        setIndoorGeomResult(null);
                                        setTransferGeom(null); // Clear stale Mapbox→Indoor transfer geometry
                                    }}
                                    onGeomChange={setIndoorGeomResult}
                                />
                            </div>
                        </Card>
                    ) : (parentSlamMapLoading && parentIsIndoorLikeDerived) || ownSlamCanvasLoading ? (
                        /* Waiting for SLAM map to load */
                        <Card title="Map" className="area-create-map-card">
                            <div style={{ textAlign: 'center', padding: 80 }}>
                                <Spin />
                                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                    Loading SLAM map…
                                </Text>
                            </div>
                        </Card>
                    ) : (
                        /* Standard GPS/Mapbox map */
                        <Card title="Map" className="area-create-map-card">
                            <div className="area-editor-map area-create-map">
                                {((geomMode === 'point' && !isStagedMixedFlow) || canEditAnchorPoint) && (
                                    <div style={{
                                        position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)',
                                        zIndex: 10, background: 'rgba(0,0,0,0.65)', color: '#fff',
                                        padding: '4px 12px', borderRadius: 12, fontSize: 12, pointerEvents: 'none'
                                    }}>
                                        {canEditAnchorPoint
                                            ? (anchorPointArmed ? 'Click once to place the SLAM position point' : 'Click Add point to place the SLAM position point')
                                            : 'Click on the map to place a pin'}
                                    </div>
                                )}
                                <div className="area-search-overlay area-create-search-overlay">
                                    <Input
                                        className="area-create-search-input"
                                        placeholder="Search for a place"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            debouncedSearch(e.target.value);
                                        }}
                                    />
                                    {searching && <div className="area-search-status">Searching...</div>}
                                    {searchResults.length > 0 && (
                                        <div className="area-search-results">
                                            {searchResults.map((r) => (
                                                <div key={r.id} className="area-search-result" onClick={() => selectSearchResult(r)}>
                                                    {r.place_name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Map
                                    ref={mapRef}
                                    mapboxAccessToken={MAPBOX_TOKEN}
                                    initialViewState={viewState}
                                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                    style={{ width: '100%', height: '100%' }}
                                    onLoad={onMapLoad}
                                >
                                    {companyRobots.map((robot) => (
                                        <Marker
                                            key={`company-robot-${robot.id}`}
                                            longitude={robot.lng}
                                            latitude={robot.lat}
                                            anchor="bottom"
                                        >
                                            <div
                                                title={`${robot.label}${robot.status ? ` · ${robot.status}` : ''}${robot.lastHeartbeat ? ` · Last update ${robot.lastHeartbeat}` : ''}`}
                                                onClick={(event) => event.stopPropagation()}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    padding: '4px 7px',
                                                    borderRadius: 14,
                                                    border: '2px solid #fff',
                                                    background: '#237804',
                                                    color: '#fff',
                                                    boxShadow: '0 2px 7px rgba(0,0,0,0.4)',
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <RobotOutlined />
                                                {robot.label}
                                            </div>
                                        </Marker>
                                    ))}
                                    {missionWaypoints.map((point, index) => (
                                        <Marker
                                            key={`mission-waypoint-${point.id}`}
                                            longitude={point.lng}
                                            latitude={point.lat}
                                            anchor="center"
                                        >
                                            <div
                                                title={point.label}
                                                onClick={(event) => event.stopPropagation()}
                                                style={{
                                                    width: 26,
                                                    height: 26,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '50%',
                                                    border: '2px solid #fff',
                                                    background: '#fa8c16',
                                                    color: '#fff',
                                                    boxShadow: '0 2px 7px rgba(0,0,0,0.45)',
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {index + 1}
                                            </div>
                                        </Marker>
                                    ))}
                                    {/* Parent SLAM map overlay (non-editable, shown in Mapbox view for mixed parent) */}
                                    {useMapboxView && parentSlamOverlayData && (
                                        <SlamMapOverlay
                                            overlayId={'parent-slam-overlay'}
                                            slamData={parentSlamOverlayData}
                                            placement={parentSlamPlacement}
                                            editable={false}
                                            opacity={0.6}
                                        />
                                    )}
                                    {/* User-selected SLAM map overlay on Mapbox GPS map */}
                                    {selectedSlamMapFull && (
                                        <SlamMapOverlay
                                            overlayId={selectedSlamMapId || 'slam'}
                                            slamData={selectedSlamMapFull}
                                            placement={slamPlacement}
                                            editable={canEditSlamPlacement}
                                            onPlacementChange={canEditSlamPlacement ? (p) => {
                                                setSlamPlacement(p);
                                            } : undefined}
                                            opacity={0.85}
                                        />
                                    )}
                                </Map>
                                {/* SLAM overlay hint for unanchored maps */}
                                {selectedSlamMapFull && !selectedSlamMapFull.geoAnchor && slamPlacement && editStage === 'slamPlacement' && (
                                    <div style={{
                                        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                                        zIndex: 10, background: 'rgba(0,0,0,0.65)', color: '#fff',
                                        padding: '4px 12px', borderRadius: 12, fontSize: 11, pointerEvents: 'none',
                                    }}>
                                        Drag the SLAM overlay to position it • Use the rotation slider below
                                    </div>
                                )}
                                {selectedSlamMapFull?.geoAnchor && (
                                    <div style={{
                                        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                                        zIndex: 10, background: 'rgba(0,100,0,0.7)', color: '#fff',
                                        padding: '4px 12px', borderRadius: 12, fontSize: 11, pointerEvents: 'none',
                                    }}>
                                        ✓ SLAM map is geo-anchored — placed automatically
                                    </div>
                                )}
                            </div>

                            {/* ── Rotation control for unanchored SLAM maps ── */}
                            {selectedSlamMapFull && !selectedSlamMapFull.geoAnchor && slamPlacement && (
                                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                            Rotate: {slamRotation}°
                                        </Text>
                                        <Slider
                                            min={0}
                                            max={359}
                                            step={1}
                                            value={slamRotation}
                                            disabled={!canEditSlamPlacement}
                                            onChange={(val: number) => {
                                                setSlamRotation(val);
                                                setSlamPlacement(prev => prev ? { ...prev, bearingDegrees: val } : prev);
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                        <Button
                                            size="small"
                                            disabled={!canEditSlamPlacement}
                                            onClick={() => {
                                                setSlamRotation(0);
                                                setSlamPlacement(prev => prev ? { ...prev, bearingDegrees: 0 } : prev);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        Position and rotation are for visual reference only — the scan dimensions are preserved (no scale or warp).
                                    </Text>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* ── Robot-captured markers panel (GPS/Mapbox view only) ── */}
                    {!showIndoorMap && canEditAreaGeometry && (
                        <Card
                            title="Robot markers"
                            size="small"
                            style={{ marginTop: 16 }}
                            extra={
                                <Space size="small">
                                    <Segmented
                                        size="small"
                                        value={markerFilterCornersOnly ? 'corners' : 'all'}
                                        onChange={(val) => setMarkerFilterCornersOnly(val === 'corners')}
                                        options={[
                                            { label: 'Area corners', value: 'corners' },
                                            { label: 'All markers', value: 'all' },
                                        ]}
                                    />
                                    <Button size="small" onClick={() => setMarkersRefreshKey((k) => k + 1)}>
                                        Refresh
                                    </Button>
                                </Space>
                            }
                        >
                            {gpsMarkers.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {markerFilterCornersOnly
                                                ? 'No area corner markers captured yet. Drive the robot to each corner and capture an "Area corner" marker from the robot console, then hit Refresh.'
                                                : 'No GPS markers found for this company.'}
                                        </Text>
                                    }
                                />
                            ) : (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px 12px' }}>
                                        {gpsMarkers.map((marker) => {
                                            const coords = marker.geom?.coordinates || [];
                                            const isUsed = usedMarkerIds.includes(marker.id);
                                            const categoryKey = marker.category?.key || marker.markerType || 'poi';
                                            const isCorner = categoryKey === 'area_corner';
                                            return (
                                                <div key={marker.id} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: isUsed ? 0.55 : 1 }}>
                                                    <span style={{
                                                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                                                        background: isCorner ? '#1677ff' : categoryKey === 'hazard' ? '#ff4d4f' : '#13c2c2',
                                                    }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <Text style={{ fontSize: 12 }} ellipsis>
                                                            {marker.label || marker.category?.name || `Marker ${marker.id}`}
                                                        </Text>
                                                        <div style={{ fontSize: 10, color: '#999' }}>
                                                            {Number(coords[1]).toFixed(6)}, {Number(coords[0]).toFixed(6)}
                                                            {marker.createdAt ? ` · ${new Date(marker.createdAt).toLocaleString()}` : ''}
                                                        </div>
                                                    </div>
                                                    <Space size={4}>
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                const [lng, lat] = coords;
                                                                if (Number.isFinite(Number(lng)) && Number.isFinite(Number(lat))) {
                                                                    mapRef.current?.flyTo({ center: [Number(lng), Number(lat)], zoom: 18 });
                                                                }
                                                            }}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            disabled={isUsed || (!isCorner && geomMode === 'polygon')}
                                                            onClick={() => appendGpsMarkerToGeometry(marker)}
                                                        >
                                                            {isUsed ? 'Added' : 'Add'}
                                                        </Button>
                                                    </Space>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {geomMode === 'polygon' && (() => {
                                        const unusedCorners = gpsMarkers.filter((m) =>
                                            (m.category?.key || m.markerType) === 'area_corner' && !usedMarkerIds.includes(m.id));
                                        if (unusedCorners.length < 3) return null;
                                        return (
                                            <Button
                                                size="small"
                                                style={{ marginTop: 10 }}
                                                onClick={() => {
                                                    const sorted = [...unusedCorners].sort((a, b) =>
                                                        new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
                                                    const limit = maxVertices || sorted.length;
                                                    sorted.slice(0, limit).forEach((m) => appendGpsMarkerToGeometry(m));
                                                }}
                                            >
                                                Use all corners ({Math.min(unusedCorners.length, maxVertices || unusedCorners.length)})
                                            </Button>
                                        );
                                    })()}
                                </>
                            )}
                        </Card>
                    )}

                    {/* ── Polygon vertex coordinate editor — below the map ── */}
                    {geomMode === 'polygon' && canEditAreaGeometry && (() => {
                        // When using indoor map, show coordinates from indoorGeomResult instead of MapboxDraw
                        const useIndoorCoords = showIndoorMap && !useMapboxView;
                        // For GPS-capable SLAM canvases, prefer GPS coordinates (same as Mapbox view)
                        const indoorHasGps = useIndoorCoords && activeSlamCanEmitGps && !!indoorGeomResult?.gpsGeom;

                        // Extract GPS coordinates from gpsGeom when parent is mixed
                        const indoorGpsCoords: { lon: number; lat: number }[] = (() => {
                            if (!indoorHasGps) return [];
                            const geom = indoorGeomResult!.gpsGeom;
                            if (geom.type === 'Polygon' && geom.coordinates?.[0]) {
                                const ring = geom.coordinates[0];
                                const trimmed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
                                    ? ring.slice(0, -1)
                                    : ring;
                                return trimmed.map((c: any) => ({ lon: c[0], lat: c[1] }));
                            }
                            return [];
                        })();

                        // Extract local metre coordinates from localGeom (for pure indoor parents)
                        const indoorLocalCoords: { x: number; y: number }[] = (() => {
                            if (!useIndoorCoords || indoorHasGps || !indoorGeomResult?.localGeom) return [];
                            const geom = indoorGeomResult.localGeom;
                            if (geom.type === 'Polygon' && geom.coordinates?.[0]) {
                                const ring = geom.coordinates[0];
                                const trimmed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
                                    ? ring.slice(0, -1)
                                    : ring;
                                return trimmed.map((c: any) => ({ x: c[0], y: c[1] }));
                            }
                            return [];
                        })();

                        // Determine display mode: GPS for mixed parent, local metres for pure indoor, MapboxDraw for outdoor
                        const displayCoords = indoorHasGps
                            ? indoorGpsCoords.map(p => ({ lon: p.lon, lat: p.lat, isGps: true }))
                            : useIndoorCoords
                                ? indoorLocalCoords.map(p => ({ lon: p.x, lat: p.y, isGps: false }))
                                : coordsOverride.map(p => ({ ...p, isGps: true }));
                        const isGpsCoords = displayCoords.length > 0 && displayCoords[0].isGps;
                        const isLocalMetres = useIndoorCoords && !indoorHasGps;

                        return (
                            <Card
                                title="Polygon coordinates"
                                size="small"
                                style={{ marginTop: 16 }}
                                extra={
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {isLocalMetres ? 'Edit values or drag corners on the indoor map' : 'Edit directly or drag vertices on the map'}
                                    </Text>
                                }
                            >
                                {displayCoords.length === 0 ? (
                                    <Empty description="Draw a polygon on the map to see and edit its coordinates" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px 16px' }}>
                                        {displayCoords.map((p, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{
                                                    minWidth: 22, height: 22, borderRadius: '50%',
                                                    background: isGpsCoords ? '#1677ff' : '#722ed1', color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                                                }}>
                                                    {idx + 1}
                                                </span>
                                                {isLocalMetres ? (
                                                    <>
                                                        <InputNumber
                                                            size="small"
                                                            value={Number(p.lon?.toFixed(2))}
                                                            step={0.1}
                                                            precision={2}
                                                            onChange={(v) => handleIndoorCoordChange(idx, 'x', v)}
                                                            style={{ flex: 1 }}
                                                            addonBefore={<span style={{ fontSize: 10, color: '#888', userSelect: 'none' }}>X(m)</span>}
                                                        />
                                                        <InputNumber
                                                            size="small"
                                                            value={Number(p.lat?.toFixed(2))}
                                                            step={0.1}
                                                            precision={2}
                                                            onChange={(v) => handleIndoorCoordChange(idx, 'y', v)}
                                                            style={{ flex: 1 }}
                                                            addonBefore={<span style={{ fontSize: 10, color: '#888', userSelect: 'none' }}>Y(m)</span>}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <InputNumber
                                                            size="small"
                                                            value={p.lon}
                                                            step={0.000001}
                                                            precision={6}
                                                            onChange={(v) => indoorHasGps ? handleIndoorGpsCoordChange(idx, 'lon', v) : handleCoordChange(idx, 'lon', v)}
                                                            style={{ flex: 1 }}
                                                            addonBefore={<span style={{ fontSize: 10, color: '#888', userSelect: 'none' }}>Lon</span>}
                                                        />
                                                        <InputNumber
                                                            size="small"
                                                            value={p.lat}
                                                            step={0.000001}
                                                            precision={6}
                                                            onChange={(v) => indoorHasGps ? handleIndoorGpsCoordChange(idx, 'lat', v) : handleCoordChange(idx, 'lat', v)}
                                                            style={{ flex: 1 }}
                                                            addonBefore={<span style={{ fontSize: 10, color: '#888', userSelect: 'none' }}>Lat</span>}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    })()}
                </Col>
            </Row>
        </div>
    );
};

export default AreaCreate;
